import type { I18nDictionaryShape, I18nResources } from 'i18n';

const ordersEnGb = {
  balePhotosPage: {
    actions: {
      edit: 'Edit',
    },
    materialNotFound: 'Material not found',
    pageTitle: 'Photo Evidence',
    sections: {
      evidence: 'Evidence',
      siteDetails: 'Site Details',
    },
  },
  bookingConfirmationDoc: {
    labels: {
      currently: 'Currently:',
      uploadDocument: 'Upload Document',
    },
    title: 'Booking Confirmation Document',
  },
  buyerAdditionalDetailsAndDocumentsSection: {
    labels: {
      commentsOnSalesOrder: 'Comments on the SO',
      internalNotes: 'Internal notes',
      purchaserExternalReferenceIndent: "Purchaser's external reference (indent)",
    },
    title: 'Additional Details and Documents',
    tooltips: {
      commentsOnSalesOrder: 'Additional information that will be displayed on the SO PDF.',
      internalNotes: 'Additional information to be shared with Safi teams.',
      purchaserExternalReferenceIndent: "Purchaser's contract number or Purchaser's own internal sales reference",
    },
  },
  buyerDealDetailsSection: {
    collectionAddressCountryLabel: 'Where is the material coming from?',
    collectionAddressCountryTooltip: 'We use this to determine which VAT number should be used.',
    incotermTooltip:
      'International commercial term that defines responsibilities and costs between Safi and the purchaser.',
    numberOfLoadsLabel: 'Number of loads',
    numberOfLoadsTooltip: 'Number of containers or trucks that will be collected.',
    numberOfTonnesLabel: 'Number of tonnes',
    numberOfTonnesTooltip: 'Calculated value. Tonnes = Tonnes per load x Number of loads.',
    title: 'Commercial Details',
    tonnesPerLoadLabel: 'Tonnes per load',
    tonnesPerLoadTooltip:
      'Average weight or payload of one container or truck, including any destination country restrictions.',
  },
  buyerFinancialDetails: {
    dueDateDirection: {
      after: 'After',
      before: 'Before',
    },
    labels: {
      bankDetails: 'Bank details',
      depositPercentageUpfront: 'Deposit Percentage upfront',
      dueDateOffset: 'Due date offset',
      invoiceFinanceProvider: 'Invoice finance provider',
      isTrialOrder: 'Is trial order',
      paymentBeforeDocRelease: 'Payment before doc release',
      paymentEvent: 'Payment Event',
      pricePerTonne: 'Price per tonne',
      sellerCurrency: 'Seller Currency',
      thirdPartyCommissionCurrency: 'Third Party Commission Currency',
      thirdPartyCommissionPerTonne: 'Third Party commission per tonne',
      variablePricing: 'Variable pricing',
    },
    paymentEvents: {
      billOfLading: 'Bill of Lading',
      collectionFromSupplier: 'Collection from supplier',
      destinationPortArrival: 'Destination port arrival',
      export: 'Export',
      icdArrival: 'ICD arrival',
      siteDelivery: 'Site Delivery',
    },
    title: 'Financial Details',
    tooltips: {
      depositPercentageUpfront: 'Percentage of the total amount that will be paid to Safi upfront, as a pre-payment.',
      dueDateDirection:
        'Number of days before or after the payment event the payment will be made to Safi by the purchaser.',
      paymentBeforeDocRelease:
        'If Yes, Safi will not release the documents until the payment for the material is received in full.',
      paymentEvent: 'Event that defines when the purchaser pays Safi for the material.',
      paymentMethod: 'Method using which purchaser will pay Safi.',
      pricePerTonne: 'Price per 1 tonne of net weight of the material.',
      thirdPartyCommissionPerTonne: 'Commission paid to a third party or agent, calculated on per-tonne basis.',
    },
  },
  buyerLogisticsSection: {
    labels: {
      allowedShippingLines: 'Allowed shipping lines',
      billType: 'Bill type',
      collectionSite: 'Collection site',
      deliverySite: 'Delivery site',
      finalDestinationSite: 'Final destination site',
      freeTimeRequired: 'Free time required',
      icd: 'ICD',
      marksAndNumbers: 'Marks and numbers',
      maxNumberOfShipments: 'Max number of shipments',
      notifyParty: 'Notify party',
      portOfDischarge: 'Port of discharge',
      portOfLoading: 'Port of loading',
      shipper: 'Shipper',
      shippingInstructionNotes: 'Shipping instruction notes',
      targetDeliveryDate: 'Target delivery date*',
    },
    title: 'Logistics',
    tooltips: {
      allowedShippingLines:
        'Carriers Safi can use to transport the material to end destination. If no carriers are selected, it is assumed any carrier is permitted.',
      collectionSite: 'Actual address of the loading site. Also, the address of where the inspection will take place.',
      deliverySite: 'Address where Safi is responsible for delivering the material to, as dictated by the Incoterm.',
      finalDestinationSite: "Address where the material is processed, i.e. one of end processor's sites.",
      freeTimeRequired:
        'Number of “free” days required at destination, before detention & demurrage charges start to apply.',
      icd: 'Inland container depot. Containers are typically transported to this location via Train.',
      maxNumberOfShipments:
        'Maximum number of shipments the buyer expects to receive, i.e. all loads have to arrive in maximum this number of shipments.',
      portOfDischarge: 'Port of arrival, at which containers are unloaded from the vessel.',
      portOfLoading: 'Port of departure, at which containers are loaded on the vessel.',
      targetDeliveryDate: 'Target date by which the buyer expects all loads to be delivered by.',
    },
  },
  buyerMaterialDetailsSection: {
    actions: {
      syncMaterialProperties: 'Sync material properties',
    },
    labels: {
      baselCodeOverride: 'Basel code override',
      containsCapsAndLabels: 'Contains caps and labels',
      dirtLevel: 'Dirt Level',
      foodGrade: 'Food grade',
      grade: 'Grade',
      material: 'Material',
      materialCustomsNameOverride: 'Material customs name override',
      materialEwcCodeOverride: 'Material ewc code override',
      materialHsCodeOverride: 'Material hs code override',
      origin: 'Origin',
      seller: 'Seller',
      siteMaterial: 'Site material',
    },
    supportedComponents: {
      addBuyer: 'Add buyer material component',
      addSeller: 'Add seller material component',
      buyerTitle: 'Buyer material components',
      sellerTitle: 'Seller material components',
    },
    title: 'Material',
    tooltips: {
      baselCodeOverride:
        'Overrides basel code on Safi documents. To be used only if the purchaser requires different basel code for customs purposes.',
      buyerMaterialSpecification: 'The buyer material specification which this sales order is for.',
      containsCapsAndLabels:
        "Only used on PET Bales. Select 'No' if the buyer or supplier of this material specifically asked for 'no caps or labels'.",
      dirtLevel: 'The amount of dirt a buyer will accept. If blank this is assumed based on the grade.',
      foodGrade: 'Confirmation this plastic is safe to reprocess for contact with food and beverages.',
      grade: 'Material grade. Attach a Quality Report if agreed as per that specification.',
      material: 'If the right spec is not present, request the Quality team to add it.',
      materialCustomsNameOverride:
        'Overrides the material name on Safi documents. To be used only if the purchaser requires different material name for customs purposes.',
      materialEwcCodeOverride:
        'Overrides the EWC code on Safi documents. To be used only if the purchaser requires different EWC code for customs purposes.',
      materialHsCodeOverride:
        'Overrides the material name on Safi documents. To be used only if different basel code is required for customs purposes.',
      origin: 'Provenance of the material.',
      seller: 'Company that produced the material Safi is purchasing.',
    },
  },
  buyerMaterialSpecifications: {
    createPackagingSpecificationDialog: {
      actions: {
        cancel: 'Cancel',
        save: 'Save',
        saveAriaLabel: 'Save packaging specification',
      },
      notifications: {
        createError: 'Error creating packaging specification',
      },
      title: 'Add a packaging specification',
    },
    createPage: {
      actions: {
        cancel: 'Cancel',
        save: 'Save',
      },
      notifications: {
        createFailed: 'Failed to create material spec: {{error}}',
      },
      title: 'Add new material specifications',
    },
    documentSection: {
      actions: {
        addDocument: 'Add document',
        replaceDocument: 'Replace document',
        viewDocument: 'View document',
      },
    },
    editPage: {
      errors: {
        idRequired: 'Buyer Material Specification Id is required',
      },
      title: 'Update Buyer Material Specification',
    },
    form: {
      labels: {
        material: 'Material',
        materialColour: 'Material colour',
        materialForm: 'Material form',
        materialGrade: 'Material grade',
        notes: 'Notes',
      },
      sections: {
        materialSpecifications: 'Material specifications',
        supportingInformation: 'Supporting information',
      },
    },
    item: {
      actions: {
        downloadAriaLabel: 'download',
        downloadSpecFile: 'Download Spec File',
        edit: 'Edit',
        hide: 'Hide',
        showMore: 'Show more',
      },
      labels: {
        notes: 'Notes:',
        packagingSpecifications: 'Packaging specifications:',
      },
      values: {
        noneAdded: 'None added',
      },
    },
    list: {
      empty: 'No material specifications found',
    },
    listPage: {
      actions: {
        addMaterialSpecification: 'Add material specification',
        manageCompany: 'Manage {{companyName}}',
      },
      sectionTitle: 'Material specifications',
      title: 'Buyer specifications',
    },
    packagingSpecificationList: {
      actions: {
        create: 'Create packaging specification',
        createAriaLabel: 'Create packaging specification',
      },
      empty: 'No packaging specifications have been created yet',
      title: 'Packaging specifications',
    },
    updateForm: {
      actions: {
        cancel: 'Cancel',
        delete: 'Delete',
        update: 'Update',
      },
      notifications: {
        updateFailed: 'Failed to update material spec: {{error}}',
      },
    },
    updatePackagingSpecificationDialog: {
      actions: {
        cancel: 'Cancel',
        update: 'Update',
        updateAriaLabel: 'Update packaging specification',
      },
      notifications: {
        updateError: 'Error updating packaging specification',
        updateSuccess: 'Packaging specification updated',
      },
      title: 'Update packaging specification',
    },
  },
  buyerPartiesSection: {
    buyerAgentLabel: 'Buyer agent',
    buyerAgentTooltip: 'Company or person who facilitated the transaction between Safi and the purchaser.',
    consigneeLabel: 'Consignee',
    endProcessorLabel: 'End processor',
    endProcessorTooltip: 'Company that processes the material.',
    purchaserLabel: 'Purchaser',
    purchaserTooltip: 'Company that agrees to purchase the material from Safi.',
    title: 'Parties',
  },
  copySessions: {
    description: {
      uploadCsvInstructions:
        'Upload a CSV with columns: <sourceTradeCompanySessionId>{{sourceTradeCompanySessionId}}</sourceTradeCompanySessionId> and <newName>{{newName}}</newName>.',
    },
    form: {
      actions: {
        startJob: 'Start job',
      },
      labels: {
        csvFile: 'CSV file',
        overrideExistingSessions:
          'Override existing sessions (if selected, all existing sessions matching by the name will be overridden)',
      },
      values: {
        executionId: 'Execution ID: {{executionId}}',
      },
    },
    notifications: {
      jobExecutionFailed: 'Job execution failed',
      jobStarted: 'Copy sessions job started',
      selectCsvFile: 'Please select a CSV file',
      startJobFailed: 'Failed to start copy sessions job',
    },
    pageTitle: 'Copy Sessions',
    states: {
      adminOnly: 'This page is restricted to admin users.',
      loading: 'Loading…',
    },
  },
  costAndRevenueSection: {
    labels: {
      comments: 'Comments',
      commission: 'Commission',
      pricePerTonne: 'Price per tonne',
    },
    title: 'Cost and Revenue',
  },
  dealAutocomplete: {
    placeholder: 'Select a deal',
  },
  dealHub: {
    dealCostDetails: {
      actions: {
        backToDeal: 'Back to deal # {{dealId}}',
      },
      columns: {
        currencyCode: 'Currency Code',
        description: 'Description',
        invoiceStatus: 'Invoice Status',
        invoiceXeroAmountPaid: 'Invoice Xero Amount Paid',
        invoiceXeroFullyPaidOn: 'Invoice Xero Fully Paid On',
        invoiceXeroId: 'Invoice Xero ID',
        lineTotal: 'Line Total',
        quantity: 'Quantity',
        status: 'Status',
        total: 'Total',
        unitAmount: 'Unit Amount',
      },
      sections: {
        invoiceLineItems: 'Invoice Line Items',
      },
    },
    dealRevenueDetails: {
      actions: {
        backToDeal: 'Back to deal # {{dealId}}',
      },
      columns: {
        description: 'Description',
        id: 'ID',
        invoiceStatus: 'Invoice Status',
        invoiceXeroAmountPaid: 'Invoice Xero Amount Paid',
        invoiceXeroFullyPaidOn: 'Invoice Xero Fully Paid On',
        invoiceXeroId: 'Invoice Xero ID',
        lineTotal: 'Line Total',
        quantity: 'Quantity',
        unitAmount: 'Unit Amount',
      },
      noRevenueDataForDeal: 'No revenue data found for deal',
      summary: '{{paid}} of {{total}} paid ({{percentage}})',
    },
  },
  dealInspectionsList: {
    states: {
      loadingRelatedInspections: 'Loading related inspections...',
      noRelatedInspections: 'No related inspections',
    },
  },
  documentGeneration: {
    actionButtons: {
      actions: {
        copiedToClipboard: 'Copied to clipboard',
        copySigningLink: 'Copy Signing Link',
        download: 'Download',
        regenerate: 'Regenerate',
        save: 'Save',
        sendSigningEmail: 'Send signing email',
        uploadDocument: 'Upload document',
      },
      notifications: {
        failedToGeneratePdf: 'Failed to generate pdf',
        failedToSendSigningEmail: 'Failed to send signing email',
        signingEmailSent: 'Signing email sent',
      },
    },
    documentDetails: {
      header: {
        lastEditedOn: 'Document last edited on {{date}}',
        reviewFor: 'Review {{documentType}} for',
        sentOn: 'Sent on {{date}}',
        signaturePending: 'Signature pending',
        signed: 'Document signed',
      },
      notFound: 'Document does not exist',
      viewerTitle: 'Document',
    },
    documentList: {
      actions: {
        review: 'Review',
        updateAlias: 'Update alias',
      },
      groupTitle: '{{modelName}} Documentation',
      title: 'Related documents',
    },
    generatePage: {
      actions: {
        generateDocument: 'Generate Document',
      },
      helperText: {
        selectDocumentType: 'Select a document type to generate',
        selectModel: 'Select a model that has documents associated with it',
      },
      labels: {
        selectCompany: 'Select Company',
        selectDocumentType: 'Select Document Type',
        selectInspection: 'Select Inspection',
        selectLoad: 'Select Load',
        selectModel: 'Select Model',
        selectPurchaseOrder: 'Select Purchase Order',
        selectSalesOrder: 'Select Sales Order',
        selectShipment: 'Select Shipment',
      },
      notifications: {
        errorGeneratingDocument: 'Error generating document',
        failedToGenerateDocument: 'Failed to generate document',
      },
      title: 'Generate Document',
    },
    history: {
      columns: {
        changedAt: 'Changed At',
        changedBy: 'Changed By',
        comment: 'Comment',
        download: 'Download',
        restore: 'Restore',
      },
      title: 'Document history for "{{title}}"',
      values: {
        current: 'Current',
        untitledDocument: 'Document',
        version: 'v{{version}}',
      },
    },
    more: {
      actions: {
        markDocumentAsSigned: 'Mark document as signed',
        openActionsMenu: 'Open document actions menu',
        updateAlias: 'Update alias',
      },
      notifications: {
        documentMarkedAsSigned: 'Document marked as signed',
        failedToMarkAsSigned: 'Failed to mark document as signed',
      },
    },
    updateAliasDialog: {
      actions: {
        cancel: 'Cancel',
        save: 'Save',
      },
      helperText: {
        alias: 'A user-friendly nickname or alternative title for the document',
      },
      labels: {
        alias: 'Alias',
      },
      notifications: {
        aliasUpdated: 'Alias updated',
        failedToUpdateAlias: 'Failed to update alias',
      },
      placeholders: {
        alias: 'Enter document alias',
      },
      title: 'Update alias',
      validation: {
        aliasMaxLength: 'Alias must be less than 40 characters',
        aliasRequired: 'Alias is required',
      },
    },
  },
  documents: {
    addButton: {
      actions: {
        addDocument: 'Add Document',
      },
      errors: {
        documentTypeRequired: 'Document type is required',
      },
      labels: {
        documentType: 'Document Type',
      },
    },
    list: {
      actions: {
        deleteAriaLabel: 'delete',
      },
      added: 'Added documents:',
      toBeAdded: 'Documents to be added:',
    },
  },
  evidenceEditPage: {
    actions: {
      cancel: 'Cancel',
    },
    materialNotFound: 'Material not found',
    pageTitle: 'Edit Evidence',
    sections: {
      evidence: 'Evidence',
      siteDetails: 'Site Details',
    },
  },
  evidencePage: {
    actions: {
      edit: 'Edit',
    },
    materialNotFound: 'Material not found',
    pageTitle: 'Manage Evidence',
    sections: {
      evidence: 'Evidence',
      siteDetails: 'Site Details',
    },
  },
  externalEvidencePage: {
    form: {
      actions: {
        confirm: 'Confirm',
      },
      labels: {
        country: 'Country',
        encordFolder: 'Encord Folder',
        sampleId: 'Sample ID',
        siteDescription: 'Site Description',
        siteMaterial: 'Site Material',
        takenAt: 'Taken At',
        tradeCompany: 'Trade Company',
      },
      notifications: {
        canNowUploadEvidence: 'You can now upload evidence',
        errorCreatingExternalEvidence: 'Error creating external evidence',
        externalEvidenceCreated: 'External evidence created',
      },
    },
    pageTitle: 'External Evidence',
    sections: {
      evidenceMetadata: 'Evidence metadata',
    },
    upload: {
      actions: {
        uploadEvidence: 'Upload evidence',
      },
      notifications: {
        uploadFailed: 'Failed to upload evidence',
        uploadSuccess: 'Successfully uploaded {{count}} photos.',
      },
      sectionTitle: 'Upload Evidence',
      values: {
        fileStatus: '{{fileName}}: {{status}}',
        loadingStatus: 'loading...',
      },
    },
  },
  financialDetailsSection: {
    depositPaymentEvents: {
      afterSuccessfulPreInspectionAndCollectionFromSupplier:
        'After successful Pre-inspection + Collection from Supplier',
      collectionFromSupplier: 'Collection from Supplier',
      uponReceiptOfSignedPo: 'Upon receipt of signed PO',
      uponReceiptOfSignedPoAndSuccessfulPreInspection: 'Upon receipt of signed PO and successful Pre-inspection',
    },
    labels: {
      commission: 'Commission',
      depositEventOffset: 'Deposit Event Offset',
      depositPaymentEvent: 'Deposit Payment Event',
      depositPercentageUpfront: 'Deposit Percentage upfront',
      dueDateOffset: 'Due date offset',
      paymentBeforeMaterialIsReleased: 'Payment before material is released',
      paymentEvent: 'Payment Event',
      paymentTerms: 'Payment Terms',
      paymentTermsComments: 'Payment terms comments',
      pricePerTonne: 'Price per tonne',
      sellerCurrency: 'Seller Currency',
      thirdPartyCommissionCurrency: 'Third Party Commission Currency',
      variablePricing: 'Variable pricing',
    },
    paymentTerms: {
      custom: 'Custom',
    },
    title: 'Financial details',
    tooltips: {
      commission: 'Commission paid to a third party or agent, calculated on per-tonne basis.',
      daysBeforeOrAfterDepositPaymentEvent:
        'Number of days before or after the deposit payment event the deposit will be paid.',
      daysBeforeOrAfterPaymentEvent:
        'Number of days before or after the payment event the payment will be made by Safi.',
      depositPaymentEvent: 'Event that defines when the deposit will be paid.',
      depositPercentageUpfront: 'Percentage of the total amount that will be paid by Safi upfront, as a pre-payment.',
      paymentBeforeMaterialIsReleased: 'If Yes, payment is required before loading can happen.',
      paymentEvent: 'Event that defines when Safi pays the seller for the material.',
      paymentMethod: 'Method using which Safi will pay the seller.',
      paymentTerms: "Select predefined payment terms or choose 'Custom' to define your own.",
      pricePerTonne: 'Price per 1 tonne of net weight of the material.',
    },
  },
  financing: {
    createPayouts: {
      actions: {
        createPayouts: 'Create Payouts',
      },
      notifications: {
        finishedProcessing: 'Finished processing pending requests',
      },
      title: 'Create Payouts for ALL Pending Payment Requests',
    },
    processingErrors: {
      row: 'Row {{row}}:',
    },
    submitPaymentRequests: {
      actions: {
        submitPaymentsToFinmid: 'Submit Payments to Finmid',
      },
      errors: {
        csvMustContainBuyerId: "CSV file must contain a 'buyer_id' column.",
      },
      labels: {
        pickAFile: 'Pick a file',
      },
      notifications: {
        submittedSuccessfully: 'Successfully submitted payment requests.',
      },
      title: 'Upload Payments CSV File',
    },
  },
  groundControl: {
    pageTitle: 'Ground Control 🚀',
    popularLinks: 'Popular Links',
    tabNavigationAriaLabel: 'ground control tabs',
  },
  incotermSpecificFields: {
    labels: {
      inlandContainerDepot: 'Inland Container Depot',
    },
    tooltips: {
      inlandContainerDepot: 'Inland container depot for delivery under CIF/CFR incoterms.',
    },
  },
  inspectionCompany: {
    actions: {
      cancel: 'Cancel',
      createCompany: 'Create company',
      save: 'Save',
    },
    fields: {
      active: 'Active',
      company: 'Company',
      email: 'Email',
      inspectors: 'Inspectors',
      name: 'Name',
      phoneNumber: 'Phone Number',
      status: 'Status',
    },
    formTitles: {
      addCompany: 'Add Company',
      addInspector: 'Add Inspector',
      editCompany: 'Edit Company',
      editInspector: 'Edit Inspector',
    },
    list: {
      noInspectors: 'No inspectors',
    },
    messages: {
      createCompanyToAssignInspectors: 'Create the company to assign inspectors.',
    },
    notifications: {
      errorMutatingCompany: 'Error mutating company',
      errorMutatingInspector: 'Error mutating inspector',
      errorSigningIn: 'Error signing in',
    },
    sectionTitles: {
      inspectionCompanies: 'Inspection companies',
      inspectors: 'Inspectors',
    },
    status: {
      active: 'Active',
      inactive: 'Inactive',
    },
    values: {
      notAvailable: 'N/A',
    },
  },
  inspectionLink: {
    actions: {
      copyInspectionLinkAriaLabel: 'copy inspection link',
      inspectionLink: 'Inspection Link',
    },
    tooltips: {
      copyToClipboard: 'Copy the inspection link to clipboard',
      onlyInspectionsWithGrade: 'Only inspections with a grade have an inspection link',
    },
  },
  inspectionLoad: {
    assigner: {
      actions: {
        assign: 'Assign',
        reassign: 'Reassign',
        unassign: 'Unassign',
      },
      confirmation: {
        message:
          'This load is already assigned to Inspection {{currentInspectionSlug}}. Are you sure you want to reassign this load this inspection.',
        title: 'Switch load {{loadName}} to inspection {{inspectionSlug}}',
      },
      empty: {
        noAssignedLoads: 'No assigned loads found for this deal.',
        noOtherInspectionLoads: 'No other inspection loads found for this deal.',
        noUnassignedLoads: 'No unassigned loads found for this deal.',
      },
      sections: {
        assignedLoads: 'Assigned loads',
        otherInspectionLoads: 'Other inspection loads',
        otherInspectionLoadsDescription: 'Loads related to this deal assigned to another inspection.',
        unassignedLoads: 'Unassigned loads',
      },
      states: {
        noLoadsForSelectedDeal: 'No loads found for selected deal',
      },
    },
    dealLoads: {
      empty: 'No loads found for this deal.',
      title: 'Deal loads',
    },
  },
  inspectionMutate: {
    actions: {
      save: 'Save',
    },
    aiSample: {
      tooltips: {
        aiLooseSampleNotAvailableForMaterial: 'AI Loose Sample is not available for this material',
        siteMaterialRequiredToEnableSamples: 'A site material is required to enable samples',
      },
    },
    labels: {
      cancellationReasonIfApplicable: 'Cancellation reason (if applicable)',
      company: 'Company',
      enableAiLooseSample: 'Enable AI Loose Sample',
      inspectedCorrectly: 'Inspected correctly',
      inspectionResult: 'Inspection Result',
      inspectionTime: 'Inspection time',
      inspector: 'Inspector',
      invalidInspectionReasonIfApplicable: 'Invalid Inspection Reason (if applicable)',
      linkToExternalReport: 'Link to external report',
      notes: 'Notes',
      siteMaterial: 'Site Material',
      status: 'Status',
      type: 'Type',
    },
    notifications: {
      changesSaved: 'Changes saved',
      errorMutatingInspection: 'Error mutating inspection',
      errorSigningIn: 'Error signing in',
    },
    title: {
      createInspection: 'Create inspection',
      editInspection: 'Edit inspection {{slug}}',
    },
    validation: {
      cancellationReasonRequiredForCancelledInspections: 'Cancellation reason is required for cancelled inspections',
      inspectionCompanyMustBeActive: 'Inspection company must be active',
      inspectorMustBelongToSelectedCompany: 'Inspector must belong to the selected company',
      linkMustBeValidUrl: 'Link must be a valid URL',
      siteMaterialRequired: 'Site material is required',
    },
    values: {
      no: 'No',
      notSpecified: 'Not specified',
      yes: 'Yes',
    },
  },
  inspectionsList: {
    actions: {
      createInspection: 'Create inspection',
    },
    title: 'Inspections',
  },
  internalHeader: {
    aria: {
      openSidebar: 'open sidebar',
    },
    links: {
      collections: 'Collections',
      dealHub: 'DealHub',
      deals: 'Deals',
      groundControl: 'Ground Control',
      qualityControl: 'Quality Control',
      shipments: 'Shipments',
    },
  },
  internalQualityReport: {
    actions: {
      linkToPublicQualityReport: 'Link to public quality report',
    },
    errors: {
      materialNotFound: 'Material not found.',
    },
  },
  loadsList: {
    actions: {
      createInspection: 'Create Inspection',
    },
    drawer: {
      title: 'Load details',
      titleWithName: 'Load {{loadName}}',
    },
    sectionTitle: 'Loads',
  },
  materialProducts: {
    deleteDialog: {
      actions: {
        delete: 'Delete',
      },
      title: 'Are you sure you want to delete it?',
    },
    details: {
      notFound: 'Material Product not found',
    },
    filters: {
      labels: {
        country: 'Country',
        materialGrade: 'Material grade',
        materialQuality: 'Material quality',
        materialSpec: 'Material spec',
      },
    },
    form: {
      actions: {
        addBuyerMaterialComponent: 'Add buyer material component',
        addSellerMaterialComponent: 'Add seller material component',
        cancel: 'Cancel',
        save: 'Save',
        update: 'Update',
      },
      labels: {
        containsCapsAndLabels: 'Contains caps and labels',
        country: 'Country',
        foodGrade: 'Food grade',
        materialCreationProcess: 'Material process',
        materialGrade: 'Material grade',
        materialOrigin: 'Material origin',
        materialSpec: 'Material spec',
      },
      sections: {
        buyerMaterialComponents: 'Buyer material components',
        sellerMaterialComponents: 'Seller material components',
      },
      title: 'Material Product',
    },
    list: {
      actions: {
        createMaterialProduct: 'Create Material Product',
      },
      title: 'Material products',
    },
  },
  packagingSpecificationForm: {
    labels: {
      alias: 'Alias',
      density: 'Density',
      height: 'Height',
      length: 'Length',
      loadWeight: 'Load weight',
      packedOn: 'Packed on',
      storageType: 'Storage type',
      strapping: 'Strapping',
      unitOfMeasurement: 'Unit of measurement',
      unitWeight: 'Unit weight',
      width: 'Width',
    },
    placeholders: {
      name: 'Name...',
    },
    sections: {
      density: 'Density',
      dimensions: 'Dimensions',
      weight: 'Weight',
    },
    units: {
      kg: 'kg',
      mt: 'MT',
    },
  },
  photoEvidenceView: {
    errors: {
      evidenceIdRequired: 'Evidence ID is required',
      evidenceNotFound: 'Evidence not found',
      noImageAvailable: 'No image available for this evidence',
    },
    pageTitle: 'Evidence Photo Preview',
    states: {
      loading: 'Loading...',
    },
  },
  profitabilityCalculator: {
    costs: {
      labels: {
        buyerAgentCommissionPerTonne: 'Buyer agent commission /t',
        estimatedTransitTimeDays: 'Est. Full (Site-Site) Transit Time Days',
        sellerAgentCommissionPerTonne: 'Seller agent commission /t',
        sellerPricePerTonne: 'Seller price /t',
        shippingAndCustomsCostPerLoad: 'Shipping & customs cost / load',
        shippingCostSource: 'Shipping cost source',
      },
      options: {
        transportCostSource: {
          estimate: 'Transport cost estimate',
          logisticsQuote: 'Logistics quote',
          other: 'Other',
        },
      },
      title: 'Costs',
    },
    currencyRates: {
      title: 'Rates',
    },
    logistics: {
      errors: {
        invalidCoordinatesForBuyerSite: 'Invalid coordinates for buyer site.',
        invalidCoordinatesForSellerSite: 'Invalid coordinates for seller site.',
      },
      labels: {
        buyer: 'Buyer',
        buyerSite: 'Buyer site',
        containerType: 'Container Type',
        destinationAddress: 'Destination address',
        incoterm: 'Incoterm',
        originAddress: 'Origin address',
        portOfDischarge: 'Port of discharge',
        portOfLoading: 'Port of loading',
        seller: 'Seller',
        sellerSite: 'Seller site',
        transportCostCurrency: 'Transport cost currency',
      },
      sections: {
        demand: 'Demand',
        supply: 'Supply',
      },
      title: 'Logistics',
    },
    material: {
      labels: {
        material: 'Material',
        tonnes: 'Tonnes',
        tonnesPerLoad: 'Tonnes / load',
      },
      title: 'Material',
    },
    missingCosts: {
      message:
        'The price estimate might be incorrect because of missing components: {{missingCosts}}. Please, reach out to Michael for a better quote.',
      options: {
        destinationIntermodalCost: 'destination intermodal cost',
        destinationTerminalHandlingCharge: 'destination terminal handling charge',
        localDestinationCost: 'local destination cost',
        localOriginCost: 'local origin cost',
        oceanFreightCost: 'ocean freight cost',
        originDocFee: 'origin doc fee',
        originIntermodalCost: 'origin intermodal cost',
        originTerminalHandlingCharge: 'origin terminal handling charge',
      },
    },
    page: {
      actions: {
        recalculate: 'Recalculate',
        resultsAreSynced: 'Results are synced',
      },
      title: 'Deal Profitability Calculator',
    },
    payment: {
      labels: {
        purchaserDepositPercentage: 'Purchaser Deposit Percentage',
        purchaserDueDateOffset: 'Purchaser Due Date Offset',
        purchaserPaymentEvent: 'Purchaser Payment Event',
        purchaserPaymentMethod: 'Purchaser Payment Method',
        sellerDepositPercentage: 'Seller Deposit Percentage',
        sellerDueDateOffset: 'Seller Due Date Offset',
        sellerPaymentEvent: 'Seller Payment Event',
      },
      options: {
        paymentEvent: {
          bolDate: 'BOL date',
          collectionFromSupplier: 'Collection from supplier',
          destinationPortArrival: 'Destination port arrival',
          export: 'Export',
          icdArrival: 'ICD arrival',
          siteDelivery: 'Site delivery',
        },
        paymentMethod: {
          documentsAgainstPayment: 'Documents Against Payment',
          telegraphicTransfer: 'Telegraphic Transfer',
        },
      },
      title: 'Payment',
    },
    resultTable: {
      costs: {
        bankFees: 'Bank Fees',
        buyerAgentCommission: 'Buyer Agent Commission',
        claimsEstimate: 'Claims Estimate',
        financingCostEstimate: 'Financing Cost Estimate',
        fxCostEstimate: 'FX Cost Estimate',
        inspectionsEstimate: 'Inspections Estimate',
        insurance: 'Insurance',
        logisticCostsEstimate: 'Logistic Costs Estimate',
        material: 'Material',
        sellerAgentCommission: 'Seller Agent Commission',
        shipping: 'Shipping',
        title: 'Costs',
        tooltips: {
          financingCostEstimate: 'Based on the payment terms and shipping route',
          fxCostEstimate: '0.2% hedge is applied to costs in EUR and revenue in USD',
        },
        totalCosts: 'Total Costs',
        unplannedLogisticCostsEstimate: 'Unplanned Logistic Costs Estimate',
      },
      dealMargin: {
        dealMargin: 'Deal Margin',
        dealMarginPercentageOfGmv: 'Deal Margin % of GMV',
        title: 'Deal Margin',
        tooltips: {
          dealMargin: 'Deal Margin = GMV - Total Costs',
        },
      },
      ntr: {
        netRevenue: 'Net Revenue',
        takeRate: 'Take Rate',
        title: 'NTR %',
        tooltips: {
          netRevenue: 'Net Revenue = GMV - Material - Third Party Commission - Shipping - FX Costs',
        },
      },
      revenues: {
        gmv: 'GMV',
        material: 'Material',
        pernRevenue: 'Pern Revenue',
        title: 'Revenues',
        tooltips: {
          pernRevenue: 'Only a percentage of material can be claimed',
        },
      },
    },
    revenues: {
      labels: {
        pernRevenuePerTonne: 'PERN revenue /t',
        purchaserPricePerTonne: 'Purchaser price /t',
      },
      title: 'Revenues',
    },
    transportCostDetails: {
      alerts: {
        noEstimatedRouteFound: 'No estimated route found',
        noTransitTimeEstimateAvailable: 'No transit time estimate available for the given route',
        transportCostBasedOnExpiredQuote:
          'The transport cost is based on an expired quote. Consider this a less accurate indication. P',
      },
      labels: {
        asOf: 'As of',
        costSource: 'Cost source',
        destinationRoadCostSource: 'Destination road cost source',
        directLandCostSource: 'Direct land cost source',
        estimatedTransitTimeFull: 'Estimated transit time (full)',
        estimatedTransitTimeRequested: 'Estimated transit time (requested)',
        oceanFreightCostSource: 'Ocean freight cost source',
        originRoadCostSource: 'Origin road cost source',
        quotedOn: 'Quoted on',
        totalTransportCost: 'Total transport cost',
        transitTimeSource: 'Transit time source',
        transportCostPerLoad: 'Transport cost per load',
      },
      sourceExplanations: {
        bothOriginAndDestinationCountryMismatch:
          'Based on historical quotes where both origin and destination countries might not match the request, but ports of loading and discharge match exactly.',
        bothPolAndPodMismatch:
          "Based on historical quotes where both port of loading and port of discharge might not match the request, but countries of the ports match the requested ports' countries.",
        continentToContinentApproximation:
          'Based on pre-defined corridors between continents, only provided as a very rough fallback estimate in case other options are not available.',
        destinationCountryMismatch:
          'Based on historical quotes where destination country might not match the requested destination, but port of discharge matches exactly.',
        fullMatch: 'Based on historical quotes matching exactly.',
        incotermsMismatch: 'Based on historical quotes where incoterms might not match the request.',
        originCountryMismatch:
          'Based on historical quotes where origin country might not match the requested origin, but port of loading matches exactly.',
        podMismatch:
          "Based on historical quotes where port of discharge might not match the requested port, but country of the port matches the requested port's country.",
        polMismatch:
          "Based on historical quotes where port of loading might not match the requested port, but country of the port matches the requested port's country.",
        roadPerKmEstimate: 'Based on the rough estimation of what distance a truck can typically cover in a day.',
      },
      title: 'Transport cost details',
      tooltips: {
        estimatedTransitTimeFull:
          'Estimated total transit time from collection site to final delivery location. This will be used for financing cost estimation.',
        estimatedTransitTimeRequested:
          'Estimated transit time based on the location selected in the above form. For reference only.',
      },
      values: {
        continentToContinentFallback: 'Continent to Continent Fallback',
        days: 'days',
        fullMatch: 'Full match',
        notAvailable: 'N/A',
        partialMatch: 'Partial match',
      },
    },
  },
  purchaseOrderCreatePage: {
    submit: 'Submit',
    subtitle: 'Please fill in the purchase order details.',
    title: 'Create Purchase Order',
  },
  purchaseOrderUpdatePage: {
    fixedPriceDialog: {
      message:
        'You are about to fix the price of this order. This action will lock in the current price and date. Do you want to proceed?',
      title: 'Confirm Fixed Price',
    },
    submit: 'Submit',
    subtitle: 'Please edit the purchase order details.',
    title: 'Edit Purchase Order {{orderId}}',
    variablePricedOrderMessage: 'To fix price, edit the Fixed price and Fixed date fields.',
    variablePricedOrderTitle: 'Variable priced order',
    warningDialog: {
      message:
        'This PO has been signed. Do you want to apply the changes?\nAfter the changes are saved, you need to Regenerate the PO PDF and send it to the customer for re-signing.',
      title: 'Warning',
    },
  },
  qualityHomePage: {
    materialNotFound: 'Material not found',
    pageTitle: 'Quality management',
    sections: {
      sessions: 'Sessions',
      siteDetails: 'Site Details',
    },
  },
  qualitySiteForm: {
    actions: {
      addNewCompany: 'Add new company',
      addNewCompanyAriaLabel: 'Add new company',
      addNewMaterial: 'Add new material',
      addNewMaterialAriaLabel: 'Add new material',
      addNewSite: 'Add new site',
      addNewSiteAriaLabel: 'Add new site',
      manage: 'Manage',
    },
    labels: {
      company: 'Company',
      material: 'Material',
      site: 'Site',
    },
  },
  riskSelect: {
    unknown: 'Unknown',
  },
  salesOrderCreatePage: {
    submit: 'Submit',
    subtitle: 'Please fill in the sales order details.',
    title: 'Create Sales Order',
  },
  salesOrderUpdatePage: {
    fixedPriceDialog: {
      message:
        'You are about to fix the price of this order. This action will lock in the current price and date. Do you want to proceed?',
      title: 'Confirm Fixed Price',
    },
    submit: 'Submit',
    subtitle: 'Please edit the sales order details for {{slug}}.',
    title: 'Update sales order {{slug}}',
    variablePricedOrderMessage: 'To fix price, edit the Fixed price and Fixed date fields.',
    variablePricedOrderTitle: 'Variable priced order',
  },
  sampleEditPage: {
    actions: {
      cancel: 'Cancel',
      submit: 'Submit',
    },
    labels: {
      sample: 'Sample',
      sampleEvidence: 'Sample Evidence',
    },
    notifications: {
      updateError: 'Failed to update sample: {{error}}',
      updateSuccess: 'Successfully updated sample',
    },
    pageTitle: 'Edit Sample',
    sections: {
      sample: 'Sample',
      siteDetails: 'Site Details',
    },
  },
  sampleMaterialEditDialog: {
    title: 'Edit Sample material',
  },
  samplePage: {
    actions: {
      edit: 'Edit',
    },
    errors: {
      siteMaterialOrAiModelNotFound: 'Site material or ai model not found',
    },
    notifications: {
      failedToUpload: 'Failed to upload {{photoName}}: {{error}}',
    },
    pageTitle: 'Manage Sample',
    sections: {
      sampleDetails: 'Sample details',
      siteDetails: 'Site Details',
    },
  },
  samplesEditPage: {
    actions: {
      cancel: 'Cancel',
    },
    pageTitle: 'Edit samples',
    sections: {
      sampleDetails: 'Sample details',
      siteDetails: 'Site Details',
    },
  },
  samplesPage: {
    actions: {
      create: 'Create',
      edit: 'Edit',
    },
    createOptions: {
      aiSample: 'AI Sample',
      labReport: 'Lab Report',
      manualSample: 'Manual Sample',
    },
    notifications: {
      createError: 'Error creating sample: {{error}}',
      sampleCreated: 'Sample created',
    },
    pageTitle: 'Manage Samples',
    sections: {
      samples: 'Samples',
      siteDetails: 'Site Details',
    },
  },
  sellerAdditionalDetailsAndDocumentsSection: {
    labels: {
      commentsOnPurchaseOrder: 'Comments on the PO',
      externalReferenceNumber: "Seller's external reference (indent)",
      internalNotes: 'Internal notes',
    },
    title: 'Additional Details and Documents section',
    tooltips: {
      commentsOnPurchaseOrder: 'Additional information that will be displayed on the PO PDF.',
      externalReferenceNumber: "Seller's contract number or Seller's own internal sales reference.",
      internalNotes: 'Additional information to be shared with Safi teams.',
    },
  },
  sellerDealDetailsSection: {
    finalDestinationCountryLabel: 'Where is the material going to?',
    incotermTooltip:
      'International commercial term that defines responsibilities and costs between Safi and the seller.',
    numberOfLoadsLabel: 'Number of loads',
    numberOfLoadsTooltip: 'Number of containers or trucks that will be collected.',
    numberOfTonnesLabel: 'Number of tonnes',
    numberOfTonnesTooltip: 'Calculated value. Tonnes = Tonnes per load x Number of loads.',
    title: 'Commercial Details',
    tonnesPerLoadLabel: 'Tonnes per load',
    tonnesPerLoadTooltip: 'Average weight or payload of one container or truck.',
  },
  sellerLogisticsSection: {
    labels: {
      productionSite: 'Production site',
      targetCollectionDate: 'Target collection date',
    },
    title: 'Logistics',
    tooltips: {
      productionSite: 'Actual address of the site the material was produced.',
      targetCollectionDate:
        "Date, committed to by the seller, by which the material will be ready for collection from seller's site.",
    },
  },
  sellerMaterialDetailsSection: {
    actions: {
      addNewSiteMaterial: 'Add new site material',
      addNewSiteMaterialAriaLabel: 'Add new site material',
    },
  },
  sellerPartiesSection: {
    exporterLabel: 'Exporter',
    exporterTooltip: 'Company that exports the material from the collection country.',
    producerLabel: 'Producer',
    producerTooltip: 'Company that produced the material Safi is purchasing.',
    sellerAgentLabel: 'Seller agent',
    sellerAgentTooltip: 'Company or person who facilitated the transaction between Safi and the seller.',
    sellerLabel: 'Seller',
    sellerTooltip: 'Company Safi is purchasing the material from.',
    title: 'Parties',
  },
  shipmentClearOverridesSection: {
    description:
      "Some of the selected loads may have overrides for fields like 'carrier booking reference'. If you want to clear those overrides, please select the 'Clear overrides on all the shipment's loads' checkbox below. If you don't select it, some loads may have values that are different from their shipment.",
    labels: {
      clearLoadsOverrides: "Clear overrides on all the shipment's loads",
    },
    title: 'Clear Load Overrides',
  },
  shipmentCreatePage: {
    actions: {
      createShipment: 'Create Shipment',
    },
    notifications: {
      shipmentCreatedSuccessfully: 'Shipment created successfully',
    },
    title: 'Create Shipment for {{salesOrderSlug}}',
  },
  shipmentFields: {
    helperText: {
      freeTimeAvailable: 'Number of days of free time available for the buyer at the port of destination',
    },
    labels: {
      additionalInvoiceDescription: 'Additional Invoice Description',
      billOfLadingNumber: 'Bill of Lading Number',
      carrier1: 'Carrier 1',
      carrier2: 'Carrier 2',
      carrier3: 'Carrier 3',
      carrierBookingReference: 'Carrier Booking Reference',
      dhlTrackingNumber: 'DHL Tracking Number',
      docsCutOffTime: 'Docs Cut-off Time',
      estimatedVesselDepartureTime: 'Estimated Vessel Departure Time',
      freeTimeAvailableDays: 'Free Time Available (Days)',
      freightForwarderReference: 'Freight Forwarder Reference',
      manualActualPortArrivalTime: 'Manual Actual Port Arrival Time',
      manualActualPortDepartureTime: 'Manual Actual Port Departure Time',
      manualEstimatedPortArrivalTime: 'Manual Estimated Port Arrival Time',
      manualEstimatedPortDepartureTime: 'Manual Estimated Port Departure Time',
      manualInvoiceNumber: 'Manual Invoice Number',
      royalMailTrackingNumber: 'Royal Mail Tracking Number',
      scheduledDeliveryTime: 'Scheduled Delivery Time',
      scheduledPortArrivalTime: 'Scheduled Port Arrival Time',
      scheduledPortDepartureTime: 'Scheduled Port Departure Time',
      selectPortOfLoading: 'Select port of loading',
      vesselNameOverride: 'Vessel Name Override',
      vgmCutOffTime: 'VGM Cut-off Time',
      voyageNumberOverride: 'Voyage Number Override',
    },
    sections: {
      general: 'General Shipment Fields',
      tracking: 'Shipment Tracking Fields',
    },
  },
  shipmentLoadsSection: {
    description: 'Only loads without a shipment will be shown. You can create an empty shipment and add loads later.',
    labels: {
      loads: 'Loads',
    },
    title: 'Select Loads',
  },
  shipmentUpdatePage: {
    actions: {
      updateShipment: 'Update Shipment',
    },
    errors: {
      shipmentNameNotSet: 'Shipment name is not set',
    },
    notifications: {
      shipmentUpdatedSuccessfully: 'Shipment updated successfully',
    },
    states: {
      loadingShipmentData: 'Loading shipment data...',
    },
    title: 'Update Shipment: {{shipmentName}}',
  },
  siteMaterials: {
    actions: {
      viewManage: 'View / Manage',
    },
    companySites: {
      noMaterialsFoundForCompany: 'No materials found for this company.',
    },
    createPage: {
      title: 'Create new site material for {{companyName}}',
    },
    form: {
      actions: {
        create: 'Create',
        createMaterialProduct: 'Create material product',
        createMaterialProductAriaLabel: 'Create material product',
        update: 'Update',
      },
      labels: {
        materialProduct: 'Material Product',
        materialSpec: 'Material spec',
        sellerLabel: 'Seller Label',
        site: 'Site',
      },
      tooltips: {
        sellerLabel: 'Seller Label (the grade the seller calls this product, e.g. 95/5)',
      },
    },
    listPage: {
      actions: {
        addSiteMaterial: 'Add site material',
        manageCompany: 'Manage {{companyName}}',
      },
      sectionTitle: 'Site materials',
      title: 'Manage site materials',
    },
    noSiteMaterialsOption: {
      createSiteMaterial: 'create a site material',
      noSiteMaterialFound: 'No site material found',
    },
    siteMaterialsList: {
      actions: {
        editAssignProduct: 'Edit/Assign Product',
      },
      columns: {
        lastUpdated: 'Last updated',
        material: 'Material',
      },
    },
    updatePage: {
      actions: {
        backToCompanySiteMaterials: 'Back to {{companyName}} site materials',
      },
      notFound: 'Site material not found',
      sectionTitle: 'Update site material',
    },
  },
  supportIssueForm: {
    actions: {
      createSupportIssue: 'Create Support Issue',
    },
    labels: {
      description: 'Description',
      priority: 'Priority',
      requestCategory: 'Request Category',
      team: 'Team',
    },
    success: {
      actions: {
        viewSupportIssue: 'View Support Issue',
      },
      description: 'You can view the issue using the link below.',
      title: 'Support Issue Created Successfully!',
    },
  },
  supportTicketsFindTickets: {
    actions: {
      openIssue: 'Open Issue',
    },
  },
  timeFilter: {
    endDate: 'End date',
    label: 'Time filter',
    separator: 'to',
    startDate: 'Start date',
  },
  tradeSignals: {
    common: {
      actions: {
        cancel: 'Cancel',
        save: 'Save',
      },
      labels: {
        currency: 'Currency',
        material: 'Material',
        materialGrade: 'Material grade',
        notes: 'Notes',
        standupNotes: 'Standup notes',
        thirdPartyCommission: 'Third party commission',
        tonnes: 'Tonnes',
      },
    },
    companySourceToggleButton: {
      options: {
        groundControlCompany: 'Ground Control company',
        hubspotLead: 'Hubspot lead',
      },
    },
    demandForm: {
      labels: {
        latestDeliveryDate: 'Latest delivery date',
        portOfDischarge: 'Port of discharge',
        selectBuyerFromGroundControl: 'Select buyer from Ground Control',
        selectBuyerFromHubspot: 'Select buyer from Hubspot',
      },
    },
    demandPage: {
      title: 'Create Demand Signal',
    },
    supplyForm: {
      actions: {
        addNewSiteMaterial: 'Add new site material',
        addNewSiteMaterialAriaLabel: 'Add new site material',
        linkToQualityReport: 'Link to quality report',
        linkToQualityReportAriaLabel: 'Link to quality report',
      },
      labels: {
        lastCollectionDate: 'Last collection date',
        saleTargetDate: 'Sale target date',
        selectPortOfLoading: 'Select port of loading',
        selectSellerFromGroundControl: 'Select seller from Ground Control',
        selectSellerFromHubspot: 'Select seller from Hubspot',
        siteMaterial: 'Site Material',
        tonnesPerLoad: 'Tonnes per load',
      },
    },
    supplyPage: {
      title: 'Create Supply Signal',
    },
  },
  transportCostsEstimator: {
    actions: {
      estimate: 'Estimate',
    },
    errors: {
      anErrorOccurred: 'An error occurred',
    },
    fields: {
      cost: 'Cost:',
      destinationPort: 'Destination port:',
      dthc: 'DTHC:',
      isDthcIncludedInOceanFreightRate: 'Is DTHC included in Ocean freight rate:',
      isOthcIncludedInOceanFreightRate: 'Is OTHC included in Ocean freight rate:',
      nearestCity: 'Nearest city:',
      oceanFreightCost: 'Ocean freight cost:',
      oceanFreightQuotedAt: 'Ocean freight quoted at:',
      originNearestCity: 'Origin nearest city:',
      originPort: 'Origin port:',
      othc: 'OTHC:',
      totalCost: 'Total cost:',
    },
    labels: {
      containerType: 'Container Type',
      currency: 'Currency',
      destinationAddress: 'Destination address',
      incoterm: 'Incoterm',
      nearestCityMaxRadius: 'Nearest city max radius',
      originAddress: 'Origin address',
      portOfDischarge: 'Port of discharge',
      portOfLoading: 'Port of loading',
    },
    pageTitle: 'Transport Costs Estimator',
    sections: {
      demand: 'Demand',
      extraOptions: 'Extra options',
      supply: 'Supply',
      transportPrices: 'Transport prices',
    },
    separatorRows: {
      destinationRoad: 'Destination Road',
      directLandTransport: 'Direct Land Transport',
      e2eQuoteSharpQuote: 'E2E Quote (Sharp Quote)',
      localDestinationCosts: 'Local Destination Costs',
      localOriginCosts: 'Local Origin Costs',
      missingCosts: 'Missing Costs',
      oceanFreightAndPortCharges: 'Ocean Freight & Port Charges',
      originRoad: 'Origin Road',
      otherWarnings: 'Other Warnings',
      total: 'Total',
    },
    values: {
      kmAway: '{{distance}}km away',
      no: 'No',
      yes: 'Yes',
    },
    warnings: {
      expiredQuote:
        'An expired quote from up to 3 months old was used for this estimation, while the normal value is 1 month. Please consider this a less accurate indication.',
    },
  },
  transportCostsFirmQuoteBulkCreate: {
    errors: {
      noFileSelected: 'No file selected',
      selectAtLeastOneQuote: 'Please select at least one quote to create',
    },
    form: {
      actions: {
        bulkCreateFirmQuotes: 'Bulk Create Firm Quotes',
        extractQuotes: 'Extract Quotes',
      },
      labels: {
        freightForwarder: 'Freight Forwarder',
        whatsappDumpFile: 'Select the WhatsApp group chat dump txt file',
      },
    },
    pageTitle: 'Bulk Create Firm Quotes',
  },
  transportMethod: {
    infoTooltip: 'Method using which the material will be transported.',
    label: 'Transport method',
    options: {
      boxTrailer: 'Box Trailer',
      container20ft: '20ft Container',
      container40ft: '40ft Container',
      curtainSide: 'Curtain side',
      walkingFloor: 'Walking Floor',
    },
  },
  variablePricingFields: {
    cashOr3MonthAverageOptions: {
      cash: 'Cash',
      lowestOfTheTwo: 'Lowest of the two',
      threeMonthAverage: '3-month average',
    },
    fixingDateOptions: {
      billOfLadingIssueDate: 'Bill of Lading issue date',
      loading: 'Loading',
      portArrival: 'Port arrival',
      portOfDeparture: 'Port of departure',
      siteDelivery: 'Site delivery',
    },
    labels: {
      cashOrThreeMonthAverage: 'Cash or 3-month average',
      fixedDate: 'Fixed date',
      fixedPrice: 'Fixed price',
      fixingDate: 'Fixing date',
      fixingDateEventEnd: 'Fixing date event - end',
      fixingDateEventStart: 'Fixing date event - start',
      fixingDateOffsetEnd: 'Fixing date offset - end',
      fixingDateOffsetStart: 'Fixing date offset - start',
      index: 'Index',
      priceAsPercentageOfIndex: 'Price as percentage of the index',
      pricePoint: 'Price point',
      useAsCustomIndex: 'Use {{inputValue}} as custom index',
      whoFixesTheDate: 'Who fixes the date',
    },
    pricePointOptions: {
      close: 'Close',
      high: 'High',
      low: 'Low',
      open: 'Open',
      volumeWeightedAverage: 'Volume-weighted average',
    },
    whoFixesDateOptions: {
      customer: 'Customer',
      fixedDate: 'Fixed Date',
      safi: 'Safi',
    },
  },
} as const;

const ordersEsEs = {
  balePhotosPage: {
    actions: {
      edit: 'Edit',
    },
    materialNotFound: 'Material not found',
    pageTitle: 'Photo Evidence',
    sections: {
      evidence: 'Evidence',
      siteDetails: 'Site Details',
    },
  },
  bookingConfirmationDoc: {
    labels: {
      currently: 'Currently:',
      uploadDocument: 'Upload Document',
    },
    title: 'Booking Confirmation Document',
  },
  buyerAdditionalDetailsAndDocumentsSection: {
    labels: {
      commentsOnSalesOrder: 'Comments on the SO',
      internalNotes: 'Internal notes',
      purchaserExternalReferenceIndent: "Purchaser's external reference (indent)",
    },
    title: 'Additional Details and Documents',
    tooltips: {
      commentsOnSalesOrder: 'Additional information that will be displayed on the SO PDF.',
      internalNotes: 'Additional information to be shared with Safi teams.',
      purchaserExternalReferenceIndent: "Purchaser's contract number or Purchaser's own internal sales reference",
    },
  },
  buyerDealDetailsSection: {
    collectionAddressCountryLabel: '¿De dónde viene el material?',
    collectionAddressCountryTooltip: 'Lo usamos para determinar qué número de IVA debe utilizarse.',
    incotermTooltip:
      'Término comercial internacional que define las responsabilidades y los costes entre Safi y el comprador.',
    numberOfLoadsLabel: 'Número de cargas',
    numberOfLoadsTooltip: 'Número de contenedores o camiones que se recogerán.',
    numberOfTonnesLabel: 'Número de toneladas',
    numberOfTonnesTooltip: 'Valor calculado. Toneladas = Toneladas por carga x Número de cargas.',
    title: 'Detalles comerciales',
    tonnesPerLoadLabel: 'Toneladas por carga',
    tonnesPerLoadTooltip:
      'Peso medio o carga útil de un contenedor o camión, incluidas las restricciones del país de destino.',
  },
  buyerFinancialDetails: {
    dueDateDirection: {
      after: 'Después',
      before: 'Antes',
    },
    labels: {
      bankDetails: 'Datos bancarios',
      depositPercentageUpfront: 'Porcentaje de depósito por adelantado',
      dueDateOffset: 'Desplazamiento de fecha de vencimiento',
      invoiceFinanceProvider: 'Proveedor de financiación de facturas',
      isTrialOrder: 'Pedido de prueba',
      paymentBeforeDocRelease: 'Pago antes de liberar documentos',
      paymentEvent: 'Evento de pago',
      pricePerTonne: 'Precio por tonelada',
      sellerCurrency: 'Moneda del vendedor',
      thirdPartyCommissionCurrency: 'Moneda de la comisión de terceros',
      thirdPartyCommissionPerTonne: 'Comisión de terceros por tonelada',
      variablePricing: 'Precio variable',
    },
    paymentEvents: {
      billOfLading: 'Conocimiento de embarque',
      collectionFromSupplier: 'Recogida del proveedor',
      destinationPortArrival: 'Llegada al puerto de destino',
      export: 'Exportación',
      icdArrival: 'Llegada al ICD',
      siteDelivery: 'Entrega en planta',
    },
    title: 'Detalles financieros',
    tooltips: {
      depositPercentageUpfront: 'Porcentaje del importe total que se pagará a Safi por adelantado, como anticipo.',
      dueDateDirection: 'Número de días antes o después del evento de pago en que el comprador pagará a Safi.',
      paymentBeforeDocRelease:
        'Si es Sí, Safi no liberará los documentos hasta que el pago del material se reciba en su totalidad.',
      paymentEvent: 'Evento que define cuándo el comprador paga a Safi por el material.',
      paymentMethod: 'Método con el que el comprador pagará a Safi.',
      pricePerTonne: 'Precio por 1 tonelada de peso neto del material.',
      thirdPartyCommissionPerTonne: 'Comisión pagada a un tercero o agente, calculada por tonelada.',
    },
  },
  buyerLogisticsSection: {
    labels: {
      allowedShippingLines: 'Allowed shipping lines',
      billType: 'Bill type',
      collectionSite: 'Collection site',
      deliverySite: 'Delivery site',
      finalDestinationSite: 'Final destination site',
      freeTimeRequired: 'Free time required',
      icd: 'ICD',
      marksAndNumbers: 'Marks and numbers',
      maxNumberOfShipments: 'Max number of shipments',
      notifyParty: 'Notify party',
      portOfDischarge: 'Port of discharge',
      portOfLoading: 'Port of loading',
      shipper: 'Shipper',
      shippingInstructionNotes: 'Shipping instruction notes',
      targetDeliveryDate: 'Target delivery date*',
    },
    title: 'Logistics',
    tooltips: {
      allowedShippingLines:
        'Carriers Safi can use to transport the material to end destination. If no carriers are selected, it is assumed any carrier is permitted.',
      collectionSite: 'Actual address of the loading site. Also, the address of where the inspection will take place.',
      deliverySite: 'Address where Safi is responsible for delivering the material to, as dictated by the Incoterm.',
      finalDestinationSite: "Address where the material is processed, i.e. one of end processor's sites.",
      freeTimeRequired:
        'Number of “free” days required at destination, before detention & demurrage charges start to apply.',
      icd: 'Inland container depot. Containers are typically transported to this location via Train.',
      maxNumberOfShipments:
        'Maximum number of shipments the buyer expects to receive, i.e. all loads have to arrive in maximum this number of shipments.',
      portOfDischarge: 'Port of arrival, at which containers are unloaded from the vessel.',
      portOfLoading: 'Port of departure, at which containers are loaded on the vessel.',
      targetDeliveryDate: 'Target date by which the buyer expects all loads to be delivered by.',
    },
  },
  buyerMaterialDetailsSection: {
    actions: {
      syncMaterialProperties: 'Sync material properties',
    },
    labels: {
      baselCodeOverride: 'Basel code override',
      containsCapsAndLabels: 'Contains caps and labels',
      dirtLevel: 'Dirt Level',
      foodGrade: 'Food grade',
      grade: 'Grade',
      material: 'Material',
      materialCustomsNameOverride: 'Material customs name override',
      materialEwcCodeOverride: 'Material ewc code override',
      materialHsCodeOverride: 'Material hs code override',
      origin: 'Origin',
      seller: 'Seller',
      siteMaterial: 'Site material',
    },
    supportedComponents: {
      addBuyer: 'Add buyer material component',
      addSeller: 'Add seller material component',
      buyerTitle: 'Buyer material components',
      sellerTitle: 'Seller material components',
    },
    title: 'Material',
    tooltips: {
      baselCodeOverride:
        'Overrides basel code on Safi documents. To be used only if the purchaser requires different basel code for customs purposes.',
      buyerMaterialSpecification: 'The buyer material specification which this sales order is for.',
      containsCapsAndLabels:
        "Only used on PET Bales. Select 'No' if the buyer or supplier of this material specifically asked for 'no caps or labels'.",
      dirtLevel: 'The amount of dirt a buyer will accept. If blank this is assumed based on the grade.',
      foodGrade: 'Confirmation this plastic is safe to reprocess for contact with food and beverages.',
      grade: 'Material grade. Attach a Quality Report if agreed as per that specification.',
      material: 'If the right spec is not present, request the Quality team to add it.',
      materialCustomsNameOverride:
        'Overrides the material name on Safi documents. To be used only if the purchaser requires different material name for customs purposes.',
      materialEwcCodeOverride:
        'Overrides the EWC code on Safi documents. To be used only if the purchaser requires different EWC code for customs purposes.',
      materialHsCodeOverride:
        'Overrides the material name on Safi documents. To be used only if different basel code is required for customs purposes.',
      origin: 'Provenance of the material.',
      seller: 'Company that produced the material Safi is purchasing.',
    },
  },
  buyerMaterialSpecifications: {
    createPackagingSpecificationDialog: {
      actions: {
        cancel: 'Cancel',
        save: 'Save',
        saveAriaLabel: 'Save packaging specification',
      },
      notifications: {
        createError: 'Error creating packaging specification',
      },
      title: 'Add a packaging specification',
    },
    createPage: {
      actions: {
        cancel: 'Cancel',
        save: 'Save',
      },
      notifications: {
        createFailed: 'Failed to create material spec: {{error}}',
      },
      title: 'Add new material specifications',
    },
    documentSection: {
      actions: {
        addDocument: 'Add document',
        replaceDocument: 'Replace document',
        viewDocument: 'View document',
      },
    },
    editPage: {
      errors: {
        idRequired: 'Buyer Material Specification Id is required',
      },
      title: 'Update Buyer Material Specification',
    },
    form: {
      labels: {
        material: 'Material',
        materialColour: 'Material colour',
        materialForm: 'Material form',
        materialGrade: 'Material grade',
        notes: 'Notes',
      },
      sections: {
        materialSpecifications: 'Material specifications',
        supportingInformation: 'Supporting information',
      },
    },
    item: {
      actions: {
        downloadAriaLabel: 'download',
        downloadSpecFile: 'Download Spec File',
        edit: 'Edit',
        hide: 'Ocultar',
        showMore: 'Mostrar más',
      },
      labels: {
        notes: 'Notes:',
        packagingSpecifications: 'Packaging specifications:',
      },
      values: {
        noneAdded: 'None added',
      },
    },
    list: {
      empty: 'No material specifications found',
    },
    listPage: {
      actions: {
        addMaterialSpecification: 'Add material specification',
        manageCompany: 'Manage {{companyName}}',
      },
      sectionTitle: 'Material specifications',
      title: 'Buyer specifications',
    },
    packagingSpecificationList: {
      actions: {
        create: 'Create packaging specification',
        createAriaLabel: 'Create packaging specification',
      },
      empty: 'No packaging specifications have been created yet',
      title: 'Packaging specifications',
    },
    updateForm: {
      actions: {
        cancel: 'Cancel',
        delete: 'Delete',
        update: 'Update',
      },
      notifications: {
        updateFailed: 'Failed to update material spec: {{error}}',
      },
    },
    updatePackagingSpecificationDialog: {
      actions: {
        cancel: 'Cancel',
        update: 'Update',
        updateAriaLabel: 'Update packaging specification',
      },
      notifications: {
        updateError: 'Error updating packaging specification',
        updateSuccess: 'Packaging specification updated',
      },
      title: 'Update packaging specification',
    },
  },
  buyerPartiesSection: {
    buyerAgentLabel: 'Agente del comprador',
    buyerAgentTooltip: 'Empresa o persona que facilitó la transacción entre Safi y el comprador.',
    consigneeLabel: 'Consignatario',
    endProcessorLabel: 'Procesador final',
    endProcessorTooltip: 'Empresa que procesa el material.',
    purchaserLabel: 'Comprador',
    purchaserTooltip: 'Empresa que acuerda comprar el material a Safi.',
    title: 'Partes',
  },
  copySessions: {
    description: {
      uploadCsvInstructions:
        'Sube un CSV con las columnas: <sourceTradeCompanySessionId>{{sourceTradeCompanySessionId}}</sourceTradeCompanySessionId> y <newName>{{newName}}</newName>.',
    },
    form: {
      actions: {
        startJob: 'Start job',
      },
      labels: {
        csvFile: 'CSV file',
        overrideExistingSessions:
          'Override existing sessions (if selected, all existing sessions matching by the name will be overridden)',
      },
      values: {
        executionId: 'Execution ID: {{executionId}}',
      },
    },
    notifications: {
      jobExecutionFailed: 'Job execution failed',
      jobStarted: 'Copy sessions job started',
      selectCsvFile: 'Please select a CSV file',
      startJobFailed: 'Failed to start copy sessions job',
    },
    pageTitle: 'Copy Sessions',
    states: {
      adminOnly: 'This page is restricted to admin users.',
      loading: 'Loading…',
    },
  },
  costAndRevenueSection: {
    labels: {
      comments: 'Comments',
      commission: 'Commission',
      pricePerTonne: 'Price per tonne',
    },
    title: 'Cost and Revenue',
  },
  dealAutocomplete: {
    placeholder: 'Select a deal',
  },
  dealHub: {
    dealCostDetails: {
      actions: {
        backToDeal: 'Back to deal # {{dealId}}',
      },
      columns: {
        currencyCode: 'Currency Code',
        description: 'Description',
        invoiceStatus: 'Invoice Status',
        invoiceXeroAmountPaid: 'Invoice Xero Amount Paid',
        invoiceXeroFullyPaidOn: 'Invoice Xero Fully Paid On',
        invoiceXeroId: 'Invoice Xero ID',
        lineTotal: 'Line Total',
        quantity: 'Quantity',
        status: 'Status',
        total: 'Total',
        unitAmount: 'Unit Amount',
      },
      sections: {
        invoiceLineItems: 'Invoice Line Items',
      },
    },
    dealRevenueDetails: {
      actions: {
        backToDeal: 'Back to deal # {{dealId}}',
      },
      columns: {
        description: 'Description',
        id: 'ID',
        invoiceStatus: 'Invoice Status',
        invoiceXeroAmountPaid: 'Invoice Xero Amount Paid',
        invoiceXeroFullyPaidOn: 'Invoice Xero Fully Paid On',
        invoiceXeroId: 'Invoice Xero ID',
        lineTotal: 'Line Total',
        quantity: 'Quantity',
        unitAmount: 'Unit Amount',
      },
      noRevenueDataForDeal: 'No revenue data found for deal',
      summary: '{{paid}} de {{total}} pagado ({{percentage}})',
    },
  },
  dealInspectionsList: {
    states: {
      loadingRelatedInspections: 'Loading related inspections...',
      noRelatedInspections: 'No related inspections',
    },
  },
  documentGeneration: {
    actionButtons: {
      actions: {
        copiedToClipboard: 'Copied to clipboard',
        copySigningLink: 'Copy Signing Link',
        download: 'Download',
        regenerate: 'Regenerate',
        save: 'Save',
        sendSigningEmail: 'Send signing email',
        uploadDocument: 'Upload document',
      },
      notifications: {
        failedToGeneratePdf: 'Failed to generate pdf',
        failedToSendSigningEmail: 'Failed to send signing email',
        signingEmailSent: 'Signing email sent',
      },
    },
    documentDetails: {
      header: {
        lastEditedOn: 'Documento editado por última vez el {{date}}',
        reviewFor: 'Revisar {{documentType}} para',
        sentOn: 'Enviado el {{date}}',
        signaturePending: 'Firma pendiente',
        signed: 'Documento firmado',
      },
      notFound: 'El documento no existe',
      viewerTitle: 'Document',
    },
    documentList: {
      actions: {
        review: 'Review',
        updateAlias: 'Update alias',
      },
      groupTitle: '{{modelName}} Documentation',
      title: 'Related documents',
    },
    generatePage: {
      actions: {
        generateDocument: 'Generate Document',
      },
      helperText: {
        selectDocumentType: 'Select a document type to generate',
        selectModel: 'Select a model that has documents associated with it',
      },
      labels: {
        selectCompany: 'Select Company',
        selectDocumentType: 'Select Document Type',
        selectInspection: 'Select Inspection',
        selectLoad: 'Select Load',
        selectModel: 'Select Model',
        selectPurchaseOrder: 'Select Purchase Order',
        selectSalesOrder: 'Select Sales Order',
        selectShipment: 'Select Shipment',
      },
      notifications: {
        errorGeneratingDocument: 'Error generating document',
        failedToGenerateDocument: 'Failed to generate document',
      },
      title: 'Generate Document',
    },
    history: {
      columns: {
        changedAt: 'Changed At',
        changedBy: 'Changed By',
        comment: 'Comment',
        download: 'Download',
        restore: 'Restore',
      },
      title: 'Document history for "{{title}}"',
      values: {
        current: 'Current',
        untitledDocument: 'Document',
        version: 'v{{version}}',
      },
    },
    more: {
      actions: {
        markDocumentAsSigned: 'Mark document as signed',
        openActionsMenu: 'Open document actions menu',
        updateAlias: 'Update alias',
      },
      notifications: {
        documentMarkedAsSigned: 'Document marked as signed',
        failedToMarkAsSigned: 'Failed to mark document as signed',
      },
    },
    updateAliasDialog: {
      actions: {
        cancel: 'Cancel',
        save: 'Save',
      },
      helperText: {
        alias: 'A user-friendly nickname or alternative title for the document',
      },
      labels: {
        alias: 'Alias',
      },
      notifications: {
        aliasUpdated: 'Alias updated',
        failedToUpdateAlias: 'Failed to update alias',
      },
      placeholders: {
        alias: 'Enter document alias',
      },
      title: 'Update alias',
      validation: {
        aliasMaxLength: 'Alias must be less than 40 characters',
        aliasRequired: 'Alias is required',
      },
    },
  },
  documents: {
    addButton: {
      actions: {
        addDocument: 'Add Document',
      },
      errors: {
        documentTypeRequired: 'Document type is required',
      },
      labels: {
        documentType: 'Document Type',
      },
    },
    list: {
      actions: {
        deleteAriaLabel: 'delete',
      },
      added: 'Added documents:',
      toBeAdded: 'Documents to be added:',
    },
  },
  evidenceEditPage: {
    actions: {
      cancel: 'Cancel',
    },
    materialNotFound: 'Material not found',
    pageTitle: 'Edit Evidence',
    sections: {
      evidence: 'Evidence',
      siteDetails: 'Site Details',
    },
  },
  evidencePage: {
    actions: {
      edit: 'Edit',
    },
    materialNotFound: 'Material not found',
    pageTitle: 'Manage Evidence',
    sections: {
      evidence: 'Evidence',
      siteDetails: 'Site Details',
    },
  },
  externalEvidencePage: {
    form: {
      actions: {
        confirm: 'Confirm',
      },
      labels: {
        country: 'Country',
        encordFolder: 'Encord Folder',
        sampleId: 'Sample ID',
        siteDescription: 'Site Description',
        siteMaterial: 'Site Material',
        takenAt: 'Taken At',
        tradeCompany: 'Trade Company',
      },
      notifications: {
        canNowUploadEvidence: 'You can now upload evidence',
        errorCreatingExternalEvidence: 'Error creating external evidence',
        externalEvidenceCreated: 'External evidence created',
      },
    },
    pageTitle: 'External Evidence',
    sections: {
      evidenceMetadata: 'Evidence metadata',
    },
    upload: {
      actions: {
        uploadEvidence: 'Upload evidence',
      },
      notifications: {
        uploadFailed: 'Failed to upload evidence',
        uploadSuccess: 'Successfully uploaded {{count}} photos.',
      },
      sectionTitle: 'Upload Evidence',
      values: {
        fileStatus: '{{fileName}}: {{status}}',
        loadingStatus: 'loading...',
      },
    },
  },
  financialDetailsSection: {
    depositPaymentEvents: {
      afterSuccessfulPreInspectionAndCollectionFromSupplier:
        'After successful Pre-inspection + Collection from Supplier',
      collectionFromSupplier: 'Collection from Supplier',
      uponReceiptOfSignedPo: 'Upon receipt of signed PO',
      uponReceiptOfSignedPoAndSuccessfulPreInspection: 'Upon receipt of signed PO and successful Pre-inspection',
    },
    labels: {
      commission: 'Commission',
      depositEventOffset: 'Deposit Event Offset',
      depositPaymentEvent: 'Deposit Payment Event',
      depositPercentageUpfront: 'Deposit Percentage upfront',
      dueDateOffset: 'Due date offset',
      paymentBeforeMaterialIsReleased: 'Payment before material is released',
      paymentEvent: 'Payment Event',
      paymentTerms: 'Payment Terms',
      paymentTermsComments: 'Payment terms comments',
      pricePerTonne: 'Price per tonne',
      sellerCurrency: 'Seller Currency',
      thirdPartyCommissionCurrency: 'Third Party Commission Currency',
      variablePricing: 'Variable pricing',
    },
    paymentTerms: {
      custom: 'Custom',
    },
    title: 'Financial details',
    tooltips: {
      commission: 'Commission paid to a third party or agent, calculated on per-tonne basis.',
      daysBeforeOrAfterDepositPaymentEvent:
        'Number of days before or after the deposit payment event the deposit will be paid.',
      daysBeforeOrAfterPaymentEvent:
        'Number of days before or after the payment event the payment will be made by Safi.',
      depositPaymentEvent: 'Event that defines when the deposit will be paid.',
      depositPercentageUpfront: 'Percentage of the total amount that will be paid by Safi upfront, as a pre-payment.',
      paymentBeforeMaterialIsReleased: 'If Yes, payment is required before loading can happen.',
      paymentEvent: 'Event that defines when Safi pays the seller for the material.',
      paymentMethod: 'Method using which Safi will pay the seller.',
      paymentTerms: "Select predefined payment terms or choose 'Custom' to define your own.",
      pricePerTonne: 'Price per 1 tonne of net weight of the material.',
    },
  },
  financing: {
    createPayouts: {
      actions: {
        createPayouts: 'Create Payouts',
      },
      notifications: {
        finishedProcessing: 'Finished processing pending requests',
      },
      title: 'Create Payouts for ALL Pending Payment Requests',
    },
    processingErrors: {
      row: 'Fila {{row}}:',
    },
    submitPaymentRequests: {
      actions: {
        submitPaymentsToFinmid: 'Submit Payments to Finmid',
      },
      errors: {
        csvMustContainBuyerId: "CSV file must contain a 'buyer_id' column.",
      },
      labels: {
        pickAFile: 'Pick a file',
      },
      notifications: {
        submittedSuccessfully: 'Successfully submitted payment requests.',
      },
      title: 'Upload Payments CSV File',
    },
  },
  groundControl: {
    pageTitle: 'Ground Control 🚀',
    popularLinks: 'Enlaces populares',
    tabNavigationAriaLabel: 'ground control tabs',
  },
  incotermSpecificFields: {
    labels: {
      inlandContainerDepot: 'Inland Container Depot',
    },
    tooltips: {
      inlandContainerDepot: 'Inland container depot for delivery under CIF/CFR incoterms.',
    },
  },
  inspectionCompany: {
    actions: {
      cancel: 'Cancelar',
      createCompany: 'Crear compañía',
      save: 'Guardar',
    },
    fields: {
      active: 'Activo',
      company: 'Compañía',
      email: 'Correo electrónico',
      inspectors: 'Inspectores',
      name: 'Nombre',
      phoneNumber: 'Número de teléfono',
      status: 'Estado',
    },
    formTitles: {
      addCompany: 'Agregar compañía',
      addInspector: 'Agregar inspector',
      editCompany: 'Editar compañía',
      editInspector: 'Editar inspector',
    },
    list: {
      noInspectors: 'Sin inspectores',
    },
    messages: {
      createCompanyToAssignInspectors: 'Crea la compañía para asignar inspectores.',
    },
    notifications: {
      errorMutatingCompany: 'Error al actualizar compañía',
      errorMutatingInspector: 'Error al actualizar inspector',
      errorSigningIn: 'Error al iniciar sesión',
    },
    sectionTitles: {
      inspectionCompanies: 'Compañías de inspección',
      inspectors: 'Inspectores',
    },
    status: {
      active: 'Activo',
      inactive: 'Inactivo',
    },
    values: {
      notAvailable: 'N/D',
    },
  },
  inspectionLink: {
    actions: {
      copyInspectionLinkAriaLabel: 'copy inspection link',
      inspectionLink: 'Inspection Link',
    },
    tooltips: {
      copyToClipboard: 'Copy the inspection link to clipboard',
      onlyInspectionsWithGrade: 'Only inspections with a grade have an inspection link',
    },
  },
  inspectionLoad: {
    assigner: {
      actions: {
        assign: 'Assign',
        reassign: 'Reassign',
        unassign: 'Unassign',
      },
      confirmation: {
        message:
          'This load is already assigned to Inspection {{currentInspectionSlug}}. Are you sure you want to reassign this load this inspection.',
        title: 'Switch load {{loadName}} to inspection {{inspectionSlug}}',
      },
      empty: {
        noAssignedLoads: 'No assigned loads found for this deal.',
        noOtherInspectionLoads: 'No other inspection loads found for this deal.',
        noUnassignedLoads: 'No unassigned loads found for this deal.',
      },
      sections: {
        assignedLoads: 'Assigned loads',
        otherInspectionLoads: 'Other inspection loads',
        otherInspectionLoadsDescription: 'Loads related to this deal assigned to another inspection.',
        unassignedLoads: 'Unassigned loads',
      },
      states: {
        noLoadsForSelectedDeal: 'No loads found for selected deal',
      },
    },
    dealLoads: {
      empty: 'No loads found for this deal.',
      title: 'Deal loads',
    },
  },
  inspectionMutate: {
    actions: {
      save: 'Save',
    },
    aiSample: {
      tooltips: {
        aiLooseSampleNotAvailableForMaterial: 'AI Loose Sample is not available for this material',
        siteMaterialRequiredToEnableSamples: 'A site material is required to enable samples',
      },
    },
    labels: {
      cancellationReasonIfApplicable: 'Cancellation reason (if applicable)',
      company: 'Company',
      enableAiLooseSample: 'Enable AI Loose Sample',
      inspectedCorrectly: 'Inspected correctly',
      inspectionResult: 'Inspection Result',
      inspectionTime: 'Inspection time',
      inspector: 'Inspector',
      invalidInspectionReasonIfApplicable: 'Invalid Inspection Reason (if applicable)',
      linkToExternalReport: 'Link to external report',
      notes: 'Notes',
      siteMaterial: 'Site Material',
      status: 'Status',
      type: 'Type',
    },
    notifications: {
      changesSaved: 'Changes saved',
      errorMutatingInspection: 'Error mutating inspection',
      errorSigningIn: 'Error signing in',
    },
    title: {
      createInspection: 'Create inspection',
      editInspection: 'Edit inspection {{slug}}',
    },
    validation: {
      cancellationReasonRequiredForCancelledInspections: 'Cancellation reason is required for cancelled inspections',
      inspectionCompanyMustBeActive: 'Inspection company must be active',
      inspectorMustBelongToSelectedCompany: 'Inspector must belong to the selected company',
      linkMustBeValidUrl: 'Link must be a valid URL',
      siteMaterialRequired: 'Site material is required',
    },
    values: {
      no: 'No',
      notSpecified: 'Not specified',
      yes: 'Yes',
    },
  },
  inspectionsList: {
    actions: {
      createInspection: 'Create inspection',
    },
    title: 'Inspections',
  },
  internalHeader: {
    aria: {
      openSidebar: 'open sidebar',
    },
    links: {
      collections: 'Collections',
      dealHub: 'DealHub',
      deals: 'Deals',
      groundControl: 'Ground Control',
      qualityControl: 'Quality Control',
      shipments: 'Shipments',
    },
  },
  internalQualityReport: {
    actions: {
      linkToPublicQualityReport: 'Link to public quality report',
    },
    errors: {
      materialNotFound: 'Material not found.',
    },
  },
  loadsList: {
    actions: {
      createInspection: 'Create Inspection',
    },
    drawer: {
      title: 'Load details',
      titleWithName: 'Load {{loadName}}',
    },
    sectionTitle: 'Loads',
  },
  materialProducts: {
    deleteDialog: {
      actions: {
        delete: 'Delete',
      },
      title: 'Are you sure you want to delete it?',
    },
    details: {
      notFound: 'Material Product not found',
    },
    filters: {
      labels: {
        country: 'Country',
        materialGrade: 'Material grade',
        materialQuality: 'Material quality',
        materialSpec: 'Material spec',
      },
    },
    form: {
      actions: {
        addBuyerMaterialComponent: 'Add buyer material component',
        addSellerMaterialComponent: 'Add seller material component',
        cancel: 'Cancel',
        save: 'Save',
        update: 'Actualizar',
      },
      labels: {
        containsCapsAndLabels: 'Contains caps and labels',
        country: 'Country',
        foodGrade: 'Food grade',
        materialCreationProcess: 'Material process',
        materialGrade: 'Material grade',
        materialOrigin: 'Material origin',
        materialSpec: 'Material spec',
      },
      sections: {
        buyerMaterialComponents: 'Buyer material components',
        sellerMaterialComponents: 'Seller material components',
      },
      title: 'Material Product',
    },
    list: {
      actions: {
        createMaterialProduct: 'Create Material Product',
      },
      title: 'Material products',
    },
  },
  packagingSpecificationForm: {
    labels: {
      alias: 'Alias',
      density: 'Density',
      height: 'Height',
      length: 'Length',
      loadWeight: 'Load weight',
      packedOn: 'Packed on',
      storageType: 'Storage type',
      strapping: 'Strapping',
      unitOfMeasurement: 'Unit of measurement',
      unitWeight: 'Unit weight',
      width: 'Width',
    },
    placeholders: {
      name: 'Name...',
    },
    sections: {
      density: 'Density',
      dimensions: 'Dimensions',
      weight: 'Weight',
    },
    units: {
      kg: 'kg',
      mt: 'MT',
    },
  },
  photoEvidenceView: {
    errors: {
      evidenceIdRequired: 'Evidence ID is required',
      evidenceNotFound: 'Evidence not found',
      noImageAvailable: 'No image available for this evidence',
    },
    pageTitle: 'Evidence Photo Preview',
    states: {
      loading: 'Loading...',
    },
  },
  profitabilityCalculator: {
    costs: {
      labels: {
        buyerAgentCommissionPerTonne: 'Buyer agent commission /t',
        estimatedTransitTimeDays: 'Est. Full (Site-Site) Transit Time Days',
        sellerAgentCommissionPerTonne: 'Seller agent commission /t',
        sellerPricePerTonne: 'Seller price /t',
        shippingAndCustomsCostPerLoad: 'Shipping & customs cost / load',
        shippingCostSource: 'Shipping cost source',
      },
      options: {
        transportCostSource: {
          estimate: 'Transport cost estimate',
          logisticsQuote: 'Logistics quote',
          other: 'Other',
        },
      },
      title: 'Costs',
    },
    currencyRates: {
      title: 'Tipos de cambio',
    },
    logistics: {
      errors: {
        invalidCoordinatesForBuyerSite: 'Invalid coordinates for buyer site.',
        invalidCoordinatesForSellerSite: 'Invalid coordinates for seller site.',
      },
      labels: {
        buyer: 'Buyer',
        buyerSite: 'Buyer site',
        containerType: 'Container Type',
        destinationAddress: 'Destination address',
        incoterm: 'Incoterm',
        originAddress: 'Origin address',
        portOfDischarge: 'Port of discharge',
        portOfLoading: 'Port of loading',
        seller: 'Seller',
        sellerSite: 'Seller site',
        transportCostCurrency: 'Transport cost currency',
      },
      sections: {
        demand: 'Demand',
        supply: 'Supply',
      },
      title: 'Logistics',
    },
    material: {
      labels: {
        material: 'Material',
        tonnes: 'Tonnes',
        tonnesPerLoad: 'Tonnes / load',
      },
      title: 'Material',
    },
    missingCosts: {
      message:
        'La estimación de precio puede ser incorrecta por componentes faltantes: {{missingCosts}}. Ponte en contacto con Michael para obtener una mejor cotización.',
      options: {
        destinationIntermodalCost: 'coste intermodal de destino',
        destinationTerminalHandlingCharge: 'cargo de manipulación en terminal de destino',
        localDestinationCost: 'coste local de destino',
        localOriginCost: 'coste local de origen',
        oceanFreightCost: 'coste de flete marítimo',
        originDocFee: 'cargo documental de origen',
        originIntermodalCost: 'coste intermodal de origen',
        originTerminalHandlingCharge: 'cargo de manipulación en terminal de origen',
      },
    },
    page: {
      actions: {
        recalculate: 'Recalculate',
        resultsAreSynced: 'Results are synced',
      },
      title: 'Deal Profitability Calculator',
    },
    payment: {
      labels: {
        purchaserDepositPercentage: 'Purchaser Deposit Percentage',
        purchaserDueDateOffset: 'Purchaser Due Date Offset',
        purchaserPaymentEvent: 'Purchaser Payment Event',
        purchaserPaymentMethod: 'Purchaser Payment Method',
        sellerDepositPercentage: 'Seller Deposit Percentage',
        sellerDueDateOffset: 'Seller Due Date Offset',
        sellerPaymentEvent: 'Seller Payment Event',
      },
      options: {
        paymentEvent: {
          bolDate: 'BOL date',
          collectionFromSupplier: 'Collection from supplier',
          destinationPortArrival: 'Destination port arrival',
          export: 'Export',
          icdArrival: 'ICD arrival',
          siteDelivery: 'Site delivery',
        },
        paymentMethod: {
          documentsAgainstPayment: 'Documents Against Payment',
          telegraphicTransfer: 'Telegraphic Transfer',
        },
      },
      title: 'Payment',
    },
    resultTable: {
      costs: {
        bankFees: 'Bank Fees',
        buyerAgentCommission: 'Buyer Agent Commission',
        claimsEstimate: 'Claims Estimate',
        financingCostEstimate: 'Financing Cost Estimate',
        fxCostEstimate: 'FX Cost Estimate',
        inspectionsEstimate: 'Inspections Estimate',
        insurance: 'Insurance',
        logisticCostsEstimate: 'Logistic Costs Estimate',
        material: 'Material',
        sellerAgentCommission: 'Seller Agent Commission',
        shipping: 'Shipping',
        title: 'Costs',
        tooltips: {
          financingCostEstimate: 'Based on the payment terms and shipping route',
          fxCostEstimate: '0.2% hedge is applied to costs in EUR and revenue in USD',
        },
        totalCosts: 'Total Costs',
        unplannedLogisticCostsEstimate: 'Unplanned Logistic Costs Estimate',
      },
      dealMargin: {
        dealMargin: 'Deal Margin',
        dealMarginPercentageOfGmv: 'Deal Margin % of GMV',
        title: 'Deal Margin',
        tooltips: {
          dealMargin: 'Deal Margin = GMV - Total Costs',
        },
      },
      ntr: {
        netRevenue: 'Net Revenue',
        takeRate: 'Take Rate',
        title: 'NTR %',
        tooltips: {
          netRevenue: 'Net Revenue = GMV - Material - Third Party Commission - Shipping - FX Costs',
        },
      },
      revenues: {
        gmv: 'GMV',
        material: 'Material',
        pernRevenue: 'Pern Revenue',
        title: 'Revenues',
        tooltips: {
          pernRevenue: 'Only a percentage of material can be claimed',
        },
      },
    },
    revenues: {
      labels: {
        pernRevenuePerTonne: 'PERN revenue /t',
        purchaserPricePerTonne: 'Purchaser price /t',
      },
      title: 'Revenues',
    },
    transportCostDetails: {
      alerts: {
        noEstimatedRouteFound: 'No estimated route found',
        noTransitTimeEstimateAvailable: 'No transit time estimate available for the given route',
        transportCostBasedOnExpiredQuote:
          'The transport cost is based on an expired quote. Consider this a less accurate indication. P',
      },
      labels: {
        asOf: 'As of',
        costSource: 'Cost source',
        destinationRoadCostSource: 'Destination road cost source',
        directLandCostSource: 'Direct land cost source',
        estimatedTransitTimeFull: 'Estimated transit time (full)',
        estimatedTransitTimeRequested: 'Estimated transit time (requested)',
        oceanFreightCostSource: 'Ocean freight cost source',
        originRoadCostSource: 'Origin road cost source',
        quotedOn: 'Quoted on',
        totalTransportCost: 'Total transport cost',
        transitTimeSource: 'Transit time source',
        transportCostPerLoad: 'Transport cost per load',
      },
      sourceExplanations: {
        bothOriginAndDestinationCountryMismatch:
          'Based on historical quotes where both origin and destination countries might not match the request, but ports of loading and discharge match exactly.',
        bothPolAndPodMismatch:
          "Based on historical quotes where both port of loading and port of discharge might not match the request, but countries of the ports match the requested ports' countries.",
        continentToContinentApproximation:
          'Based on pre-defined corridors between continents, only provided as a very rough fallback estimate in case other options are not available.',
        destinationCountryMismatch:
          'Based on historical quotes where destination country might not match the requested destination, but port of discharge matches exactly.',
        fullMatch: 'Based on historical quotes matching exactly.',
        incotermsMismatch: 'Based on historical quotes where incoterms might not match the request.',
        originCountryMismatch:
          'Based on historical quotes where origin country might not match the requested origin, but port of loading matches exactly.',
        podMismatch:
          "Based on historical quotes where port of discharge might not match the requested port, but country of the port matches the requested port's country.",
        polMismatch:
          "Based on historical quotes where port of loading might not match the requested port, but country of the port matches the requested port's country.",
        roadPerKmEstimate: 'Based on the rough estimation of what distance a truck can typically cover in a day.',
      },
      title: 'Transport cost details',
      tooltips: {
        estimatedTransitTimeFull:
          'Estimated total transit time from collection site to final delivery location. This will be used for financing cost estimation.',
        estimatedTransitTimeRequested:
          'Estimated transit time based on the location selected in the above form. For reference only.',
      },
      values: {
        continentToContinentFallback: 'Continent to Continent Fallback',
        days: 'days',
        fullMatch: 'Full match',
        notAvailable: 'N/A',
        partialMatch: 'Partial match',
      },
    },
  },
  purchaseOrderCreatePage: {
    submit: 'Enviar',
    subtitle: 'Completa los detalles de la orden de compra.',
    title: 'Crear orden de compra',
  },
  purchaseOrderUpdatePage: {
    fixedPriceDialog: {
      message:
        'Estás a punto de fijar el precio de esta orden. Esta acción bloqueará el precio y la fecha actuales. ¿Quieres continuar?',
      title: 'Confirmar precio fijo',
    },
    submit: 'Enviar',
    subtitle: 'Edita los detalles de la orden de compra.',
    title: 'Editar orden de compra {{orderId}}',
    variablePricedOrderMessage: 'Para fijar el precio, edita los campos Precio fijo y Fecha fija.',
    variablePricedOrderTitle: 'Orden con precio variable',
    warningDialog: {
      message:
        'Esta OC ha sido firmada. ¿Quieres aplicar los cambios?\nDespués de guardar los cambios, debes regenerar el PDF de la OC y enviarlo al cliente para volver a firmarlo.',
      title: 'Advertencia',
    },
  },
  qualityHomePage: {
    materialNotFound: 'Material not found',
    pageTitle: 'Quality management',
    sections: {
      sessions: 'Sessions',
      siteDetails: 'Site Details',
    },
  },
  qualitySiteForm: {
    actions: {
      addNewCompany: 'Add new company',
      addNewCompanyAriaLabel: 'Add new company',
      addNewMaterial: 'Add new material',
      addNewMaterialAriaLabel: 'Add new material',
      addNewSite: 'Add new site',
      addNewSiteAriaLabel: 'Add new site',
      manage: 'Manage',
    },
    labels: {
      company: 'Company',
      material: 'Material',
      site: 'Site',
    },
  },
  riskSelect: {
    unknown: 'Desconocido',
  },
  salesOrderCreatePage: {
    submit: 'Enviar',
    subtitle: 'Completa los detalles de la orden de venta.',
    title: 'Crear orden de venta',
  },
  salesOrderUpdatePage: {
    fixedPriceDialog: {
      message:
        'Estás a punto de fijar el precio de esta orden. Esta acción bloqueará el precio y la fecha actuales. ¿Quieres continuar?',
      title: 'Confirmar precio fijo',
    },
    submit: 'Enviar',
    subtitle: 'Edita los detalles de la orden de venta para {{slug}}.',
    title: 'Actualizar orden de venta {{slug}}',
    variablePricedOrderMessage: 'Para fijar el precio, edita los campos Precio fijo y Fecha fija.',
    variablePricedOrderTitle: 'Orden con precio variable',
  },
  sampleEditPage: {
    actions: {
      cancel: 'Cancelar',
      submit: 'Enviar',
    },
    labels: {
      sample: 'Muestra',
      sampleEvidence: 'Evidencia de muestra',
    },
    notifications: {
      updateError: 'Error al actualizar la muestra: {{error}}',
      updateSuccess: 'Muestra actualizada correctamente',
    },
    pageTitle: 'Editar muestra',
    sections: {
      sample: 'Muestra',
      siteDetails: 'Detalles del sitio',
    },
  },
  sampleMaterialEditDialog: {
    title: 'Edit Sample material',
  },
  samplePage: {
    actions: {
      edit: 'Edit',
    },
    errors: {
      siteMaterialOrAiModelNotFound: 'Site material or ai model not found',
    },
    notifications: {
      failedToUpload: 'Failed to upload {{photoName}}: {{error}}',
    },
    pageTitle: 'Manage Sample',
    sections: {
      sampleDetails: 'Sample details',
      siteDetails: 'Site Details',
    },
  },
  samplesEditPage: {
    actions: {
      cancel: 'Cancel',
    },
    pageTitle: 'Edit samples',
    sections: {
      sampleDetails: 'Sample details',
      siteDetails: 'Site Details',
    },
  },
  samplesPage: {
    actions: {
      create: 'Create',
      edit: 'Edit',
    },
    createOptions: {
      aiSample: 'AI Sample',
      labReport: 'Lab Report',
      manualSample: 'Manual Sample',
    },
    notifications: {
      createError: 'Error creating sample: {{error}}',
      sampleCreated: 'Sample created',
    },
    pageTitle: 'Manage Samples',
    sections: {
      samples: 'Samples',
      siteDetails: 'Site Details',
    },
  },
  sellerAdditionalDetailsAndDocumentsSection: {
    labels: {
      commentsOnPurchaseOrder: 'Comments on the PO',
      externalReferenceNumber: 'Referencia externa del vendedor (indent)',
      internalNotes: 'Internal notes',
    },
    title: 'Additional Details and Documents section',
    tooltips: {
      commentsOnPurchaseOrder: 'Additional information that will be displayed on the PO PDF.',
      externalReferenceNumber: "Seller's contract number or Seller's own internal sales reference.",
      internalNotes: 'Additional information to be shared with Safi teams.',
    },
  },
  sellerDealDetailsSection: {
    finalDestinationCountryLabel: '¿A dónde va el material?',
    incotermTooltip:
      'Término comercial internacional que define las responsabilidades y los costes entre Safi y el vendedor.',
    numberOfLoadsLabel: 'Número de cargas',
    numberOfLoadsTooltip: 'Número de contenedores o camiones que se recogerán.',
    numberOfTonnesLabel: 'Número de toneladas',
    numberOfTonnesTooltip: 'Valor calculado. Toneladas = Toneladas por carga x Número de cargas.',
    title: 'Detalles comerciales',
    tonnesPerLoadLabel: 'Toneladas por carga',
    tonnesPerLoadTooltip: 'Peso medio o carga útil de un contenedor o camión.',
  },
  sellerLogisticsSection: {
    labels: {
      productionSite: 'Production site',
      targetCollectionDate: 'Target collection date',
    },
    title: 'Logistics',
    tooltips: {
      productionSite: 'Actual address of the site the material was produced.',
      targetCollectionDate:
        "Date, committed to by the seller, by which the material will be ready for collection from seller's site.",
    },
  },
  sellerMaterialDetailsSection: {
    actions: {
      addNewSiteMaterial: 'Add new site material',
      addNewSiteMaterialAriaLabel: 'Add new site material',
    },
  },
  sellerPartiesSection: {
    exporterLabel: 'Exportador',
    exporterTooltip: 'Empresa que exporta el material desde el país de recogida.',
    producerLabel: 'Productor',
    producerTooltip: 'Empresa que produjo el material que Safi está comprando.',
    sellerAgentLabel: 'Agente del vendedor',
    sellerAgentTooltip: 'Empresa o persona que facilitó la transacción entre Safi y el vendedor.',
    sellerLabel: 'Vendedor',
    sellerTooltip: 'Empresa de la que Safi compra el material.',
    title: 'Partes',
  },
  shipmentClearOverridesSection: {
    description:
      "Some of the selected loads may have overrides for fields like 'carrier booking reference'. If you want to clear those overrides, please select the 'Clear overrides on all the shipment's loads' checkbox below. If you don't select it, some loads may have values that are different from their shipment.",
    labels: {
      clearLoadsOverrides: "Clear overrides on all the shipment's loads",
    },
    title: 'Clear Load Overrides',
  },
  shipmentCreatePage: {
    actions: {
      createShipment: 'Crear envío',
    },
    notifications: {
      shipmentCreatedSuccessfully: 'Envío creado correctamente',
    },
    title: 'Crear envío para {{salesOrderSlug}}',
  },
  shipmentFields: {
    helperText: {
      freeTimeAvailable: 'Number of days of free time available for the buyer at the port of destination',
    },
    labels: {
      additionalInvoiceDescription: 'Additional Invoice Description',
      billOfLadingNumber: 'Bill of Lading Number',
      carrier1: 'Carrier 1',
      carrier2: 'Carrier 2',
      carrier3: 'Carrier 3',
      carrierBookingReference: 'Carrier Booking Reference',
      dhlTrackingNumber: 'DHL Tracking Number',
      docsCutOffTime: 'Docs Cut-off Time',
      estimatedVesselDepartureTime: 'Estimated Vessel Departure Time',
      freeTimeAvailableDays: 'Free Time Available (Days)',
      freightForwarderReference: 'Freight Forwarder Reference',
      manualActualPortArrivalTime: 'Manual Actual Port Arrival Time',
      manualActualPortDepartureTime: 'Manual Actual Port Departure Time',
      manualEstimatedPortArrivalTime: 'Manual Estimated Port Arrival Time',
      manualEstimatedPortDepartureTime: 'Manual Estimated Port Departure Time',
      manualInvoiceNumber: 'Manual Invoice Number',
      royalMailTrackingNumber: 'Royal Mail Tracking Number',
      scheduledDeliveryTime: 'Scheduled Delivery Time',
      scheduledPortArrivalTime: 'Scheduled Port Arrival Time',
      scheduledPortDepartureTime: 'Scheduled Port Departure Time',
      selectPortOfLoading: 'Select port of loading',
      vesselNameOverride: 'Vessel Name Override',
      vgmCutOffTime: 'VGM Cut-off Time',
      voyageNumberOverride: 'Voyage Number Override',
    },
    sections: {
      general: 'General Shipment Fields',
      tracking: 'Shipment Tracking Fields',
    },
  },
  shipmentLoadsSection: {
    description: 'Only loads without a shipment will be shown. You can create an empty shipment and add loads later.',
    labels: {
      loads: 'Loads',
    },
    title: 'Select Loads',
  },
  shipmentUpdatePage: {
    actions: {
      updateShipment: 'Update Shipment',
    },
    errors: {
      shipmentNameNotSet: 'Shipment name is not set',
    },
    notifications: {
      shipmentUpdatedSuccessfully: 'Shipment updated successfully',
    },
    states: {
      loadingShipmentData: 'Loading shipment data...',
    },
    title: 'Update Shipment: {{shipmentName}}',
  },
  siteMaterials: {
    actions: {
      viewManage: 'Ver / Gestionar',
    },
    companySites: {
      noMaterialsFoundForCompany: 'No materials found for this company.',
    },
    createPage: {
      title: 'Crear nuevo material del sitio para {{companyName}}',
    },
    form: {
      actions: {
        create: 'Create',
        createMaterialProduct: 'Create material product',
        createMaterialProductAriaLabel: 'Create material product',
        update: 'Update',
      },
      labels: {
        materialProduct: 'Material Product',
        materialSpec: 'Material spec',
        sellerLabel: 'Seller Label',
        site: 'Site',
      },
      tooltips: {
        sellerLabel: 'Seller Label (the grade the seller calls this product, e.g. 95/5)',
      },
    },
    listPage: {
      actions: {
        addSiteMaterial: 'Add site material',
        manageCompany: 'Manage {{companyName}}',
      },
      sectionTitle: 'Site materials',
      title: 'Manage site materials',
    },
    noSiteMaterialsOption: {
      createSiteMaterial: 'create a site material',
      noSiteMaterialFound: 'No site material found',
    },
    siteMaterialsList: {
      actions: {
        editAssignProduct: 'Edit/Assign Product',
      },
      columns: {
        lastUpdated: 'Last updated',
        material: 'Material',
      },
    },
    updatePage: {
      actions: {
        backToCompanySiteMaterials: 'Back to {{companyName}} site materials',
      },
      notFound: 'Site material not found',
      sectionTitle: 'Update site material',
    },
  },
  supportIssueForm: {
    actions: {
      createSupportIssue: 'Create Support Issue',
    },
    labels: {
      description: 'Description',
      priority: 'Priority',
      requestCategory: 'Request Category',
      team: 'Team',
    },
    success: {
      actions: {
        viewSupportIssue: 'Ver incidencia de soporte',
      },
      description: 'Puedes ver la incidencia usando el siguiente enlace.',
      title: '¡Incidencia de soporte creada correctamente!',
    },
  },
  supportTicketsFindTickets: {
    actions: {
      openIssue: 'Open Issue',
    },
  },
  timeFilter: {
    endDate: 'Fecha de fin',
    label: 'Time filter',
    separator: 'a',
    startDate: 'Fecha de inicio',
  },
  tradeSignals: {
    common: {
      actions: {
        cancel: 'Cancel',
        save: 'Save',
      },
      labels: {
        currency: 'Currency',
        material: 'Material',
        materialGrade: 'Material grade',
        notes: 'Notes',
        standupNotes: 'Standup notes',
        thirdPartyCommission: 'Third party commission',
        tonnes: 'Tonnes',
      },
    },
    companySourceToggleButton: {
      options: {
        groundControlCompany: 'Empresa de Ground Control',
        hubspotLead: 'Lead de HubSpot',
      },
    },
    demandForm: {
      labels: {
        latestDeliveryDate: 'Latest delivery date',
        portOfDischarge: 'Port of discharge',
        selectBuyerFromGroundControl: 'Select buyer from Ground Control',
        selectBuyerFromHubspot: 'Select buyer from Hubspot',
      },
    },
    demandPage: {
      title: 'Create Demand Signal',
    },
    supplyForm: {
      actions: {
        addNewSiteMaterial: 'Add new site material',
        addNewSiteMaterialAriaLabel: 'Add new site material',
        linkToQualityReport: 'Link to quality report',
        linkToQualityReportAriaLabel: 'Link to quality report',
      },
      labels: {
        lastCollectionDate: 'Last collection date',
        saleTargetDate: 'Sale target date',
        selectPortOfLoading: 'Select port of loading',
        selectSellerFromGroundControl: 'Select seller from Ground Control',
        selectSellerFromHubspot: 'Select seller from Hubspot',
        siteMaterial: 'Site Material',
        tonnesPerLoad: 'Tonnes per load',
      },
    },
    supplyPage: {
      title: 'Create Supply Signal',
    },
  },
  transportCostsEstimator: {
    actions: {
      estimate: 'Estimate',
    },
    errors: {
      anErrorOccurred: 'An error occurred',
    },
    fields: {
      cost: 'Cost:',
      destinationPort: 'Destination port:',
      dthc: 'DTHC:',
      isDthcIncludedInOceanFreightRate: 'Is DTHC included in Ocean freight rate:',
      isOthcIncludedInOceanFreightRate: 'Is OTHC included in Ocean freight rate:',
      nearestCity: 'Nearest city:',
      oceanFreightCost: 'Ocean freight cost:',
      oceanFreightQuotedAt: 'Ocean freight quoted at:',
      originNearestCity: 'Origin nearest city:',
      originPort: 'Origin port:',
      othc: 'OTHC:',
      totalCost: 'Total cost:',
    },
    labels: {
      containerType: 'Container Type',
      currency: 'Currency',
      destinationAddress: 'Destination address',
      incoterm: 'Incoterm',
      nearestCityMaxRadius: 'Nearest city max radius',
      originAddress: 'Origin address',
      portOfDischarge: 'Port of discharge',
      portOfLoading: 'Port of loading',
    },
    pageTitle: 'Transport Costs Estimator',
    sections: {
      demand: 'Demand',
      extraOptions: 'Extra options',
      supply: 'Supply',
      transportPrices: 'Transport prices',
    },
    separatorRows: {
      destinationRoad: 'Destination Road',
      directLandTransport: 'Direct Land Transport',
      e2eQuoteSharpQuote: 'E2E Quote (Sharp Quote)',
      localDestinationCosts: 'Local Destination Costs',
      localOriginCosts: 'Local Origin Costs',
      missingCosts: 'Missing Costs',
      oceanFreightAndPortCharges: 'Ocean Freight & Port Charges',
      originRoad: 'Origin Road',
      otherWarnings: 'Other Warnings',
      total: 'Total',
    },
    values: {
      kmAway: '{{distance}}km away',
      no: 'No',
      yes: 'Yes',
    },
    warnings: {
      expiredQuote:
        'An expired quote from up to 3 months old was used for this estimation, while the normal value is 1 month. Please consider this a less accurate indication.',
    },
  },
  transportCostsFirmQuoteBulkCreate: {
    errors: {
      noFileSelected: 'No file selected',
      selectAtLeastOneQuote: 'Please select at least one quote to create',
    },
    form: {
      actions: {
        bulkCreateFirmQuotes: 'Bulk Create Firm Quotes',
        extractQuotes: 'Extract Quotes',
      },
      labels: {
        freightForwarder: 'Freight Forwarder',
        whatsappDumpFile: 'Select the WhatsApp group chat dump txt file',
      },
    },
    pageTitle: 'Bulk Create Firm Quotes',
  },
  transportMethod: {
    infoTooltip: 'Method using which the material will be transported.',
    label: 'Transport method',
    options: {
      boxTrailer: 'Box Trailer',
      container20ft: '20ft Container',
      container40ft: '40ft Container',
      curtainSide: 'Curtain side',
      walkingFloor: 'Walking Floor',
    },
  },
  variablePricingFields: {
    cashOr3MonthAverageOptions: {
      cash: 'Cash',
      lowestOfTheTwo: 'Lowest of the two',
      threeMonthAverage: '3-month average',
    },
    fixingDateOptions: {
      billOfLadingIssueDate: 'Bill of Lading issue date',
      loading: 'Loading',
      portArrival: 'Port arrival',
      portOfDeparture: 'Port of departure',
      siteDelivery: 'Site delivery',
    },
    labels: {
      cashOrThreeMonthAverage: 'Cash or 3-month average',
      fixedDate: 'Fixed date',
      fixedPrice: 'Fixed price',
      fixingDate: 'Fixing date',
      fixingDateEventEnd: 'Fixing date event - end',
      fixingDateEventStart: 'Fixing date event - start',
      fixingDateOffsetEnd: 'Fixing date offset - end',
      fixingDateOffsetStart: 'Fixing date offset - start',
      index: 'Index',
      priceAsPercentageOfIndex: 'Price as percentage of the index',
      pricePoint: 'Price point',
      useAsCustomIndex: 'Use {{inputValue}} as custom index',
      whoFixesTheDate: 'Who fixes the date',
    },
    pricePointOptions: {
      close: 'Close',
      high: 'High',
      low: 'Low',
      open: 'Open',
      volumeWeightedAverage: 'Volume-weighted average',
    },
    whoFixesDateOptions: {
      customer: 'Customer',
      fixedDate: 'Fixed Date',
      safi: 'Safi',
    },
  },
} satisfies I18nDictionaryShape<typeof ordersEnGb>;

const authEnGb = {
  notifications: {
    errorSigningIn: 'Error signing in',
    errorVerifyingPasscode: 'Error verifying passcode',
  },
  shared: {
    goBack: 'Go back',
    marketingSubtitle:
      'You are only a few steps away from creating your account and getting started with {{brandName}}.',
    marketingTitle: 'Trade recyclables the smart way',
    next: 'Next',
  },
  signIn: {
    newToSafi: 'New to {{brandName}}? <createAccountLink>Create your account</createAccountLink>',
  },
  signInVerify: {
    emailPasscodeHint: "Check your email. We've sent a one-time passcode to sign in to {{brandName}}.",
    phonePasscodeHint: "Check your phone. We've sent a one-time passcode to sign in to {{brandName}}.",
    welcomeBack: 'Welcome back!',
  },
  signUp: {
    alreadyHaveAccount: 'Already have an account? <signInLink>Sign in</signInLink>',
  },
  signUpCompany: {
    description: 'You can update your company information at any time in the future.',
    title: 'Setup your company',
  },
  signUpSuccess: {
    bookCall: 'You can also <introCallLink>book an intro call with us.</introCallLink>',
    explorePlatform: 'For now, continue to explore our quality platform.',
    paragraphOne:
      "We're thrilled to have you on board! We want to make sure that you get the most value out of {{brandName}}'s offering.",
    paragraphTwo:
      'Our representative will be in touch soon to dive deeper into your needs and discuss the next steps to start trading with {{brandName}}.',
    title: "We'll be in touch",
  },
  signUpVerify: {
    emailPasscodeHint: "Check your email. We've sent a one-time passcode to sign in to {{brandName}}.",
    welcome: 'Welcome to {{brandName}}!',
  },
} as const;

const authEsEs = {
  notifications: {
    errorSigningIn: 'Error al iniciar sesión',
    errorVerifyingPasscode: 'Error al verificar el código',
  },
  shared: {
    goBack: 'Volver',
    marketingSubtitle: 'Estás a solo unos pasos de crear tu cuenta y empezar con {{brandName}}.',
    marketingTitle: 'Comercia reciclables de forma inteligente',
    next: 'Siguiente',
  },
  signIn: {
    newToSafi: '¿Nuevo en {{brandName}}? <createAccountLink>Crea tu cuenta</createAccountLink>',
  },
  signInVerify: {
    emailPasscodeHint:
      'Revisa tu correo electrónico. Te hemos enviado un código de un solo uso para iniciar sesión en {{brandName}}.',
    phonePasscodeHint:
      'Revisa tu teléfono. Te hemos enviado un código de un solo uso para iniciar sesión en {{brandName}}.',
    welcomeBack: '¡Bienvenido de nuevo!',
  },
  signUp: {
    alreadyHaveAccount: '¿Ya tienes una cuenta? <signInLink>Inicia sesión</signInLink>',
  },
  signUpCompany: {
    description: 'Puedes actualizar la información de tu empresa en cualquier momento en el futuro.',
    title: 'Configura tu empresa',
  },
  signUpSuccess: {
    bookCall: 'También puedes <introCallLink>reservar una llamada introductoria con nosotros.</introCallLink>',
    explorePlatform: 'Por ahora, sigue explorando nuestra plataforma de calidad.',
    paragraphOne:
      '¡Estamos encantados de tenerte a bordo! Queremos asegurarnos de que obtengas el máximo valor de la oferta de {{brandName}}.',
    paragraphTwo:
      'Nuestro representante se pondrá en contacto contigo pronto para profundizar en tus necesidades y comentar los próximos pasos para empezar a comerciar con {{brandName}}.',
    title: 'Nos pondremos en contacto',
  },
  signUpVerify: {
    emailPasscodeHint:
      'Revisa tu correo electrónico. Te hemos enviado un código de un solo uso para iniciar sesión en {{brandName}}.',
    welcome: '¡Bienvenido a {{brandName}}!',
  },
} satisfies I18nDictionaryShape<typeof authEnGb>;

const authDeDe = {
  notifications: {
    errorSigningIn: 'Fehler bei der Anmeldung',
    errorVerifyingPasscode: 'Fehler beim Bestätigen des Codes',
  },
  shared: {
    goBack: 'Zurück',
    marketingSubtitle:
      'Du bist nur noch wenige Schritte davon entfernt, dein Konto zu erstellen und mit {{brandName}} loszulegen.',
    marketingTitle: 'Recyclingmaterial intelligent handeln',
    next: 'Weiter',
  },
  signIn: {
    newToSafi: 'Neu bei {{brandName}}? <createAccountLink>Konto erstellen</createAccountLink>',
  },
  signInVerify: {
    emailPasscodeHint:
      'Prüfe dein E-Mail-Postfach. Wir haben dir einen einmaligen Code gesendet, um dich bei {{brandName}} anzumelden.',
    phonePasscodeHint:
      'Prüfe dein Telefon. Wir haben dir einen einmaligen Code gesendet, um dich bei {{brandName}} anzumelden.',
    welcomeBack: 'Willkommen zurück!',
  },
  signUp: {
    alreadyHaveAccount: 'Du hast bereits ein Konto? <signInLink>Anmelden</signInLink>',
  },
  signUpCompany: {
    description: 'Du kannst deine Unternehmensdaten später jederzeit aktualisieren.',
    title: 'Unternehmen einrichten',
  },
  signUpSuccess: {
    bookCall: 'Du kannst auch <introCallLink>einen Einführungstermin mit uns buchen.</introCallLink>',
    explorePlatform: 'Erkunde vorerst weiter unsere Qualitätsplattform.',
    paragraphOne:
      'Wir freuen uns sehr, dich an Bord zu haben. Wir möchten sicherstellen, dass du den größtmöglichen Nutzen aus dem Angebot von {{brandName}} ziehst.',
    paragraphTwo:
      'Unser Team meldet sich bald bei dir, um deine Anforderungen im Detail zu besprechen und die nächsten Schritte für den Handel mit {{brandName}} zu planen.',
    title: 'Wir melden uns bei dir',
  },
  signUpVerify: {
    emailPasscodeHint:
      'Prüfe dein E-Mail-Postfach. Wir haben dir einen einmaligen Code gesendet, um dich bei {{brandName}} anzumelden.',
    welcome: 'Willkommen bei {{brandName}}!',
  },
} satisfies I18nDictionaryShape<typeof authEnGb>;

const authSrRs = {
  notifications: {
    errorSigningIn: 'Greška pri prijavljivanju',
    errorVerifyingPasscode: 'Greška pri proveri koda',
  },
  shared: {
    goBack: 'Nazad',
    marketingSubtitle: 'Još samo nekoliko koraka te deli od kreiranja naloga i početka rada sa {{brandName}}.',
    marketingTitle: 'Pametniji način za trgovinu reciklabilima',
    next: 'Dalje',
  },
  signIn: {
    newToSafi: 'Novi ste na {{brandName}}? <createAccountLink>Napravite nalog</createAccountLink>',
  },
  signInVerify: {
    emailPasscodeHint: 'Proverite e-poštu. Poslali smo vam jednokratni kod za prijavu na {{brandName}}.',
    phonePasscodeHint: 'Proverite telefon. Poslali smo vam jednokratni kod za prijavu na {{brandName}}.',
    welcomeBack: 'Dobro došli nazad!',
  },
  signUp: {
    alreadyHaveAccount: 'Već imate nalog? <signInLink>Prijavite se</signInLink>',
  },
  signUpCompany: {
    description: 'Podatke o kompaniji možete da ažurirate u bilo kom trenutku kasnije.',
    title: 'Podesite kompaniju',
  },
  signUpSuccess: {
    bookCall: 'Možete i <introCallLink>da zakažete uvodni poziv sa nama.</introCallLink>',
    explorePlatform: 'Za sada nastavite da istražujete našu platformu za kvalitet.',
    paragraphOne:
      'Drago nam je što ste sa nama! Želimo da iz ponude kompanije {{brandName}} izvučete maksimalnu vrednost.',
    paragraphTwo:
      'Naš predstavnik će vam se uskoro javiti da detaljnije prođe kroz vaše potrebe i naredne korake za početak trgovine sa {{brandName}}.',
    title: 'Javićemo vam se',
  },
  signUpVerify: {
    emailPasscodeHint: 'Proverite e-poštu. Poslali smo vam jednokratni kod za prijavu na {{brandName}}.',
    welcome: 'Dobro došli u {{brandName}}!',
  },
} satisfies I18nDictionaryShape<typeof authEnGb>;

const authItIt = {
  notifications: {
    errorSigningIn: "Errore durante l'accesso",
    errorVerifyingPasscode: 'Errore nella verifica del codice',
  },
  shared: {
    goBack: 'Indietro',
    marketingSubtitle: "Sei a pochi passi dalla creazione del tuo account e dall'inizio con {{brandName}}.",
    marketingTitle: 'Un modo piu intelligente per commerciare materiali riciclabili',
    next: 'Avanti',
  },
  signIn: {
    newToSafi: 'Nuovo su {{brandName}}? <createAccountLink>Crea un account</createAccountLink>',
  },
  signInVerify: {
    emailPasscodeHint: 'Controlla la tua email. Ti abbiamo inviato un codice monouso per accedere a {{brandName}}.',
    phonePasscodeHint: 'Controlla il tuo telefono. Ti abbiamo inviato un codice monouso per accedere a {{brandName}}.',
    welcomeBack: 'Bentornato!',
  },
  signUp: {
    alreadyHaveAccount: 'Hai gia un account? <signInLink>Accedi</signInLink>',
  },
  signUpCompany: {
    description: "Puoi aggiornare i dettagli dell'azienda in qualsiasi momento.",
    title: "Configura l'azienda",
  },
  signUpSuccess: {
    bookCall: 'Puoi anche <introCallLink>prenotare una chiamata introduttiva con noi.</introCallLink>',
    explorePlatform: 'Per ora, continua a esplorare la nostra piattaforma per la qualita.',
    paragraphOne:
      "Siamo molto felici di averti a bordo. Vogliamo assicurarci che tu ottenga il massimo valore dall'offerta di {{brandName}}.",
    paragraphTwo:
      'Il nostro team ti contattera presto per discutere le tue esigenze in dettaglio e pianificare i prossimi passi per il commercio con {{brandName}}.',
    title: 'Ti contatteremo',
  },
  signUpVerify: {
    emailPasscodeHint: 'Controlla la tua email. Ti abbiamo inviato un codice monouso per registrarti su {{brandName}}.',
    welcome: 'Benvenuto su {{brandName}}!',
  },
} satisfies I18nDictionaryShape<typeof authEnGb>;

declare global {
  interface I18nNamespaceRegistry {
    auth: typeof authEnGb;
    orders: typeof ordersEnGb;
  }
}

export const internalI18nResources: I18nResources<'auth' | 'orders'> = {
  'de-DE': {
    auth: authDeDe,
    orders: ordersEnGb,
  },
  'en-GB': {
    auth: authEnGb,
    orders: ordersEnGb,
  },
  'es-ES': {
    auth: authEsEs,
    orders: ordersEsEs,
  },
  'it-IT': {
    auth: authItIt,
    orders: ordersEnGb,
  },
  'sr-RS': {
    auth: authSrRs,
    orders: ordersEnGb,
  },
};
